// ─────────────────────────────────────────────────────────────────────────────
// IMPORTS
// React nos dá os "hooks": funções especiais que controlam o estado e o ciclo
// de vida de um componente sem precisar escrever classes.
//   • useState  → guarda um valor que, ao mudar, refaz a tela (re-render)
//   • useEffect → roda código quando o componente aparece ou quando dependências mudam
//   • useRef    → guarda uma referência a um elemento DOM (ex: input) sem re-render
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';

// useNavigate é do React Router: nos deixa mudar de rota via código (sem <Link>).
import { useNavigate } from 'react-router-dom';

// Importamos todas as funções de API de um único arquivo central.
// Isso evita repetir a URL base e os headers em cada componente.
import { api } from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES GLOBAIS
// Ficam fora dos componentes porque nunca mudam — são dados fixos do negócio.
// ─────────────────────────────────────────────────────────────────────────────

// Ordem exata em que os setores aparecem no Kanban e no fluxo de produção.
const SETORES = [
  'Atendimento',
  'Arte Finalista',
  'Pré Produção',
  'Impressão Offset',
  'Impressão Digital',
  'Impressão Fora',
  'Corte',
  'Acabamento',
  'Expedição',
];

// Tipos de impressão válidos (usados para detectar quando pedir a escolha na bipagem)
const TIPOS_IMPRESSAO = ['Impressão Offset', 'Impressão Digital', 'Impressão Fora'];

const PROXIMO_SETOR_FIXO = {
  'Pré Produção':      'Impressão Offset',
  'Impressão Offset':  'Corte',
};

// Mapa de setor → cor.
const COR_SETOR = {
  'Atendimento':       '#00c9db',
  'Arte Finalista':    '#a78bfa',
  'Pré Produção':      '#fbbf24',
  'Impressão Offset':  '#4f8ef7',
  'Impressão Digital': '#818cf8',
  'Impressão Fora':    '#c084fc',
  'Acabamento':        '#4ade80',
  'Corte':             '#f97316',
  'Expedição':         '#34d399',
  'Concluído':         '#4ade80',
};

// Setor anterior para exibir o botão de retrocesso no ModalItem.
// Corte, Acabamento e Expedição não entram aqui: cada um pode ser alcançado por
// mais de um caminho agora — o backend resolve pelo histórico real de bipagens.
const SETOR_ANTERIOR_MAP = {
  'Arte Finalista':    'Atendimento',
  'Pré Produção':      'Arte Finalista',
  'Impressão Offset':  'Pré Produção',
  'Impressão Digital': 'Arte Finalista',
  'Impressão Fora':    'Arte Finalista',
};

function getSetorAnterior(setor) {
  return SETOR_ANTERIOR_MAP[setor] || null;
}

// Converte um timestamp ISO (entrada_em) em string legível de tempo decorrido.
// Ex: "45min", "2h 15min", "3 dias"
function formatarTempo(entradaEm) {
  if (!entradaEm) return null;
  const minutos = Math.floor((Date.now() - new Date(entradaEm)) / 60000);
  if (minutos < 1)   return '< 1min';
  if (minutos < 60)  return `${minutos}min`;
  const horas = Math.floor(minutos / 60);
  const min   = minutos % 60;
  if (horas < 24)    return min > 0 ? `${horas}h ${min}min` : `${horas}h`;
  const dias = Math.floor(horas / 24);
  const horasRestantes = horas % 24;
  return horasRestantes > 0
    ? `${dias}d ${horasRestantes}h`
    : `${dias} dia${dias > 1 ? 's' : ''}`;
}

// Thresholds de urgência — atualizados pelo Kanban ao carregar as configurações
let urgenciaConfig = { baixaMin: 240, mediaMin: 720 };

function urgencia(entradaEm) {
  if (!entradaEm) return null;
  const minutos = Math.floor((Date.now() - new Date(entradaEm)) / 60000);
  if (minutos < 1) return null;
  if (minutos < urgenciaConfig.baixaMin) return { cor: '#4ade80', label: 'Baixa urgência' };
  if (minutos < urgenciaConfig.mediaMin) return { cor: '#f0a500', label: 'Média urgência' };
  return { cor: '#e05555', label: 'Alta urgência' };
}

// Lista de processos que cada setor precisa concluir antes de avançar.
// Esta mesma lista existe no backend (itensController.js) para validação dupla:
// o frontend orienta o usuário e o backend garante a regra mesmo que alguém
// tente burlar pelo Postman ou outro cliente HTTP.
const PROCESSOS_SETOR = {
  'Atendimento': [
    'Receber pedido do cliente',
    'Conferir briefing e especificações',
    'Aprovar orçamento',
    'Registrar ordem de serviço',
  ],
  'Arte Finalista': [
    'Receber arquivo do cliente',
    'Verificar resolução e sangria',
    'Montar arte finalizada',
    'Enviar prova para aprovação',
    'Registrar aprovação recebida',
  ],
  'Pré Produção': [
    'Montar chapa de impressão',
  ],
  'Impressão Offset': [
    'Conferir cores CMYK',
    'Imprimir amostra teste',
    'Aprovar amostra de cor',
    'Imprimir tiragem completa',
  ],
  'Impressão Digital': [
    'Calibrar impressora',
    'Conferir arquivo de impressão',
    'Imprimir amostra teste',
    'Aprovar qualidade de impressão',
    'Imprimir tiragem completa',
  ],
  'Impressão Fora': [
    'Enviar arquivo para fornecedor',
    'Confirmar recebimento pelo fornecedor',
    'Aprovar prova do fornecedor',
    'Aguardar conclusão da impressão',
    'Conferir material recebido',
  ],
  'Acabamento': [
    'Dobrar / Vincar',
    'Aplicar acabamento especial',
    'Conferir qualidade final',
    'Embalar produto',
  ],
  'Corte': [
    'Conferir medidas do projeto',
    'Calibrar guilhotina',
    'Realizar corte',
    'Conferir amostra cortada',
  ],
  'Expedição': ['Concluído'],
};

// ─────────────────────────────────────────────────────────────────────────────
// SISTEMA DE TEMAS (claro / escuro)
// Função pura: recebe um booleano e devolve um objeto com todas as cores.
// Todos os componentes recebem esse objeto via prop "t" e usam t.cardBg,
// t.textPrimary, etc. — assim, trocar o tema é trocar apenas esse objeto.
// ─────────────────────────────────────────────────────────────────────────────
function getTema(claro) {
  // Tema claro: tons quentes de bege/creme inspirados no papel offset
  if (claro) return {
    pageBg:          '#f0ede7', // fundo geral da página
    headerBg:        '#faf8f4', // fundo do cabeçalho
    headerBorder:    '#e8e3da', // linha inferior do cabeçalho
    colBg:           '#e8e3da', // fundo das colunas do Kanban
    colBorder:       '#ddd8ce', // borda das colunas
    colHeaderBorder: '#d5cfc4', // linha separando o título do corpo da coluna
    cardBg:          '#fff',    // fundo dos cartões de item
    cardHover:       '#f9f7f3', // fundo do cartão ao passar o mouse
    textHeading:     '#2a2622', // títulos principais
    textPrimary:     '#3a3530', // texto corrido
    textSecondary:   '#999',    // texto secundário / meta-informações
    textMuted:       '#ccc',    // texto quase invisível (separadores, "vazio")
    textCode:        '#f0a500', // número do item em fonte monospace
    liveDotColor:    '#22c55e', // ponto verde "ao vivo" no header
    logoutColor:     '#aaa',    // cor do botão de sair
    logoutBorder:    '#ddd8ce', // borda do botão de sair
    toggleBg:        '#e0dbd2', // fundo do botão de tema
    toggleBorder:    '#ccc8be', // borda do botão de tema
    toggleColor:     '#6b6560', // ícone do botão de tema
    panelBg:         '#faf8f4', // fundo do painel de bipagem lateral
    panelBorder:     '#e0dbd2', // borda esquerda do painel
    inputBg:         '#ede9e2', // fundo do campo de texto
    inputBorder:     '#d5cfc4', // borda do campo de texto
    inputText:       '#2a2622', // cor do texto digitado
  };

  // Tema escuro padrão: preto quase absoluto com acentos âmbar
  return {
    pageBg:          '#0d0d0d',
    headerBg:        '#111',
    headerBorder:    '#1a1a1a',
    colBg:           '#141414',
    colBorder:       '#1e1e1e',
    colHeaderBorder: '#1c1c1c',
    cardBg:          '#1e1e1e',
    cardHover:       '#252525',
    textHeading:     '#e0e0e0',
    textPrimary:     '#d8d8d8',
    textSecondary:   '#888',
    textMuted:       '#2a2a2a',
    textCode:        '#f0a500', // número do item em fonte monospace
    liveDotColor:    '#4ade80',
    logoutColor:     '#555',
    logoutBorder:    '#222',
    toggleBg:        'transparent',
    toggleBorder:    '#2a2a2a',
    toggleColor:     '#666',
    panelBg:         '#111',
    panelBorder:     '#1a1a1a',
    inputBg:         '#181818',
    inputBorder:     '#2a2a2a',
    inputText:       '#e8e8e8',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTES DE ÍCONE
// São componentes React que retornam SVG inline.
// SVG inline nos dá controle total de cor via "stroke='currentColor'",
// que herda a cor do CSS pai — sem precisar de arquivos de imagem.
// ─────────────────────────────────────────────────────────────────────────────

// Ícone do botão de alternância de tema:
// • Se "claro" for true  → mostra sol (tema está claro, clique vai escurecer)
// • Se "claro" for false → mostra lua (tema está escuro, clique vai clarear)
function IconeTema({ claro }) {
  if (claro) return (
    // Ícone de sol: círculo central + 8 raios
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
  // Ícone de lua crescente
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

// Ícone de check (✓) usado na mensagem de sucesso do painel de bipagem
function IconeCheck() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARTÃO DE ITEM
// Representa um único item dentro de uma coluna do Kanban.
// Props recebidas:
//   • item   → objeto com numero, descricao, ordemNumero, cliente, setor_atual
//   • cor    → cor do setor (usada na borda esquerda colorida)
//   • t      → objeto de tema
//   • onClick → função chamada ao clicar, recebe o item como argumento
// ─────────────────────────────────────────────────────────────────────────────
function CartaoItem({ item, cor, t, onClick, mostrarDetalhes = true }) {
  const [hovered, setHovered] = useState(false);
  const [compacto, setCompacto] = useState(false);
  const [localDetalhes, setLocalDetalhes] = useState(true);
  const aguardandoChapa = item.setor_atual === 'Pré Produção' && !!item.entrada_em && !item.chapa_buscada;
  const temaClaro = t.cardBg === '#fff';
  const corInformacao = aguardandoChapa
    ? (temaClaro ? '#160706' : '#fff6f4')
    : t.textSecondary;
  const corTextoPrincipal = aguardandoChapa
    ? (temaClaro ? '#160706' : '#fff6f4')
    : t.textPrimary;
  const corNumero = aguardandoChapa
    ? (temaClaro ? '#160706' : '#ffe1a3')
    : t.textCode;
  const corSeloOS = aguardandoChapa
    ? (temaClaro ? '#160706' : '#fff6f4')
    : '#a78bfa';
  const estiloChapa = aguardandoChapa ? {
    '--chapa-fundo-base': hovered ? t.cardHover : t.cardBg,
    '--chapa-fundo-alerta': temaClaro ? '#e4483d' : '#c02420',
    '--chapa-fundo-reduzido': temaClaro ? '#fbe0dd' : '#432321',
  } : {};

  // Global muda → todos os cards sincronizam; depois cada um pode ser alterado individualmente
  useEffect(() => {
    setLocalDetalhes(mostrarDetalhes);
  }, [mostrarDetalhes]);

  if (compacto) {
    return (
      <div
        onClick={() => onClick(item)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={aguardandoChapa ? 'card-aguardando-chapa' : ''}
        style={{
          ...estiloChapa,
          background: hovered ? t.cardHover : t.cardBg,
          borderRadius: '7px', padding: '7px 10px',
          borderLeft: `3px solid ${aguardandoChapa ? '#ef4444' : cor}`,
          transform: hovered ? 'translateY(-1px)' : 'translateY(0)',
          transition: 'background 0.15s, transform 0.15s',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden',
        }}
      >
        {item.dataEntrega ? (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', color: corNumero, fontWeight: 700, flexShrink: 0 }}>
            {dataEntregaNoFormatoBrasileiro(item.dataEntrega)}
          </span>
        ) : <span aria-hidden="true" />}
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.63rem', color: corSeloOS, background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.22)', borderRadius: '3px', padding: '1px 5px', flexShrink: 0 }}>
          {item.ordemNumero}
        </span>
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: corTextoPrincipal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {item.descricao}
        </span>
        {item.ultima_anotacao_texto && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={COR_SETOR[item.ultima_anotacao_setor] || '#888'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        )}
        {(() => {
          if (!item.entrada_em) {
            return (
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.6rem', color: '#f0a500', background: 'rgba(240,165,0,0.1)', border: '1px solid rgba(240,165,0,0.3)', borderRadius: '4px', padding: '1px 5px', flexShrink: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25, textAlign: 'center' }}>
                <span>Aguardando Entrada</span>
                <span>{formatarTempo(item.criado_em)}</span>
              </span>
            );
          }
          if (aguardandoChapa) {
            const u = urgencia(item.entrada_em);
            return (
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', color: corInformacao, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {formatarTempo(item.entrada_em)}
                {u && <span style={{ marginLeft: '4px', fontSize: '0.6rem', background: u.cor + '18', border: `1px solid ${u.cor}40`, borderRadius: '10px', padding: '1px 5px' }}>{u.label}</span>}
              </span>
            );
          }
          const u = urgencia(item.entrada_em);
          return (
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', color: u?.cor || cor, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
              {formatarTempo(item.entrada_em)}
              {u && <span style={{ marginLeft: '4px', fontSize: '0.6rem', background: u.cor + '18', border: `1px solid ${u.cor}40`, borderRadius: '10px', padding: '1px 5px' }}>{u.label}</span>}
            </span>
          );
        })()}
        <button
          onClick={(e) => { e.stopPropagation(); setCompacto(false); }}
          title="Expandir"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: corInformacao, flexShrink: 0, display: 'flex', alignItems: 'center' }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
        </button>
      </div>
    );
  }

  const isArteFinalista = item.setor_atual === 'Arte Finalista';
  const temCartela          = isArteFinalista && item.precisa_cartela && !item.cartela_confirmada;
  const cartelaConfirmadaCard = isArteFinalista && item.precisa_cartela && item.cartela_confirmada;
  const corCartela = item.tipo_cartela === '4x0' ? '#3b82f6' : '#a78bfa';
  const cardClass  = aguardandoChapa ? 'card-aguardando-chapa' : '';
  const borderLeft = aguardandoChapa
    ? '6px solid #ef4444'
    : temCartela
      ? `6px solid ${corCartela}`
      : cartelaConfirmadaCard
        ? '4px solid #4ade80'
        : `4px solid ${cor}`;
  const mostrar = localDetalhes;

  return (
    <div
      onClick={() => onClick(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cardClass}
      style={{
        ...estiloChapa,
        background: hovered ? t.cardHover : t.cardBg,
        borderRadius: '10px',
        padding: '16px 18px',
        borderLeft,
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'background 0.15s, transform 0.15s',
        cursor: 'pointer',
      }}
    >
      {/* Linha 1: data de entrega + olho (hover) + badge O.S */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        {item.dataEntrega ? (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8rem', color: corNumero, fontWeight: 700, letterSpacing: '0.02em', whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
            <span style={{ color: corInformacao, fontFamily: "'DM Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.04em' }}>ENTREGA</span>
            {dataEntregaNoFormatoBrasileiro(item.dataEntrega)}
          </span>
        ) : <span aria-hidden="true" />}
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
          <button
            onClick={(e) => { e.stopPropagation(); setLocalDetalhes(v => !v); }}
            title={localDetalhes ? 'Ocultar detalhes' : 'Mostrar detalhes'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '1px',
              color: localDetalhes ? corInformacao : '#f0a500',
              display: 'flex', alignItems: 'center',
              opacity: hovered ? 0.65 : 0,
              transition: 'opacity 0.15s',
            }}
          >
            {localDetalhes ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            )}
          </button>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: '0.72rem', fontWeight: 700,
            color: corSeloOS, background: 'rgba(167,139,250,0.1)',
            border: '1px solid rgba(167,139,250,0.22)', borderRadius: '4px',
            padding: '2px 7px', letterSpacing: '0.03em',
          }}>
            O.S {item.ordemNumero}
          </span>
          {typeof item.qtde === 'number' && (
            <span style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', fontWeight: 600,
              color: corInformacao, background: 'rgba(136,136,136,0.1)',
              border: '1px solid rgba(136,136,136,0.35)', borderRadius: '4px',
              padding: '2px 6px', letterSpacing: '0.02em', whiteSpace: 'nowrap',
            }}>
              {item.qtde} un
            </span>
          )}
        </div>
      </div>

      {isArteFinalista && !item.arte_finalista_responsavel && (
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', color: corInformacao, margin: '0 0 8px' }}>
          Sem responsável definido
        </p>
      )}

      {/* Linha 2: descrição — oculta no modo privado */}
      {mostrar && (
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', color: corTextoPrincipal, margin: 0, marginBottom: '9px', lineHeight: 1.4, fontWeight: 500 }}>
          {item.descricao}
        </p>
      )}

      {/* Linha 3: cliente + data de entrada no sistema */}
      {mostrar && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginBottom: '9px', minWidth: 0 }}>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: corInformacao, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
            {item.cliente}
          </span>
          {item.criado_em && (
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', color: corInformacao, flexShrink: 0, whiteSpace: 'nowrap' }}>
              {new Date(item.criado_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
            </span>
          )}
        </div>
      )}

      {/* Última anotação — oculta no modo privado */}
      {mostrar && item.ultima_anotacao_texto && (() => {
        const corAnot = COR_SETOR[item.ultima_anotacao_setor] || '#888';
        return (
          <div style={{
            background: corAnot + '12', border: `1px solid ${corAnot}30`,
            borderLeft: `3px solid ${corAnot}`,
            borderRadius: '6px', padding: '7px 10px', marginBottom: '9px',
          }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', color: corAnot, margin: '0 0 2px 0', fontWeight: 700, opacity: 0.85 }}>
              {item.ultima_anotacao_setor} · {item.ultima_anotacao_por}
            </p>
            <p style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: corAnot,
              margin: 0, lineHeight: 1.4, opacity: 0.95,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {item.ultima_anotacao_texto}
            </p>
          </div>
        );
      })()}

      {/* Linha final */}
      {(() => {
        const u = item.entrada_em ? urgencia(item.entrada_em) : null;

        const badgePendente = !item.entrada_em
          ? { texto: 'Aguardando Entrada', cor: '#f0a500', bg: 'rgba(240,165,0,0.1)', border: 'rgba(240,165,0,0.3)' }
          : aguardandoChapa
          ? { texto: 'Aguardando Chapa', cor: corInformacao, bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)' }
          : null;

        const timerEl = item.entrada_em ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={aguardandoChapa ? corInformacao : (u?.cor || cor)} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: aguardandoChapa ? corInformacao : (u?.cor || cor), fontWeight: 600 }}>
              {formatarTempo(item.entrada_em)}
            </span>
            {u && (
              <span
                className={u.label === 'Alta urgência' ? 'badge-alta-urgencia' : ''}
                style={{ fontSize: '0.56rem', fontWeight: 700, color: aguardandoChapa ? corInformacao : u.cor, background: u.cor + '18', border: `1px solid ${u.cor}40`, borderRadius: '20px', padding: '1px 5px', whiteSpace: 'nowrap' }}
              >
                {u.label}
              </span>
            )}
          </div>
        ) : null;

        const badgeEl = badgePendente ? (
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', fontWeight: 700, color: badgePendente.cor, background: badgePendente.bg, border: `1px solid ${badgePendente.border}`, borderRadius: '5px', padding: '3px 7px', whiteSpace: 'nowrap', letterSpacing: '0.02em', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.3 }}>
            <span>{badgePendente.texto}</span>
            {!item.entrada_em && <span>{formatarTempo(item.criado_em)}</span>}
          </span>
        ) : null;

        return mostrar ? (
          /* Detalhes visíveis: tipo impressão + estado/timer lado a lado */
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {item.tipo_impressao && (
              <span style={{
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700,
                color: COR_SETOR[item.tipo_impressao] || '#888',
                background: (COR_SETOR[item.tipo_impressao] || '#888') + '15',
                border: `1px solid ${(COR_SETOR[item.tipo_impressao] || '#888')}35`,
                borderRadius: '4px', padding: '2px 8px', whiteSpace: 'nowrap',
              }}>
                {item.tipo_impressao}
              </span>
            )}
            {badgeEl || timerEl}
          </div>
        ) : (
          /* Detalhes ocultos: cliente em cima, estado embaixo */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: 0, width: '100%' }}>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: corInformacao, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'center' }}>
              {item.cliente}
            </span>
            {badgeEl || timerEl}
          </div>
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARTÃO DE O.S AGRUPADA — PROTÓTIPO (só usado na coluna Expedição)
// Agrupa vários itens da mesma O.S num único card, expansível ao clicar.
// ─────────────────────────────────────────────────────────────────────────────
function itemEstaPronto(item) {
  // Sem checklist configurado pro setor = nada a concluir = considera pronto.
  if (!item.processos_total) return true;
  return item.processos_concluidos >= item.processos_total;
}

function CartaoOrdemAgrupada({ grupo, cor, t, onAbrirModal, onRecarregar, pendentes = [] }) {
  const [aberto, setAberto] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [despachando, setDespachando] = useState(false);
  const [progresso, setProgresso] = useState(null); // { feitos, total }
  const [erroDespacho, setErroDespacho] = useState('');

  const prontos = grupo.itens.filter(itemEstaPronto).length;
  const total = grupo.itens.length;
  const faltamChegar = pendentes.length;
  // Só está de verdade pronto se o checklist dos itens visíveis estiver completo
  // E não sobrar nenhum item da mesma O.S esperando pra chegar na Expedição —
  // essa segunda condição é a mesma regra que o backend já aplica (bipar()).
  const tudoPronto = prontos === total && faltamChegar === 0;
  const corPronto = '#4ade80';

  // PROTÓTIPO: despacha todos os itens da O.S em sequência, reaproveitando
  // o mesmo endpoint PATCH /itens/:numero/bipar que o modal individual usa.
  // Para no primeiro erro (não tenta desfazer os que já deram certo).
  async function despacharTudo() {
    setConfirmando(false);
    setDespachando(true);
    setErroDespacho('');
    let feitos = 0;
    setProgresso({ feitos, total });
    for (const item of grupo.itens) {
      try {
        await api.bipar(item.numero, 'saida');
        feitos++;
        setProgresso({ feitos, total });
      } catch (err) {
        setDespachando(false);
        setErroDespacho(`${feitos} de ${total} despachados — item ${item.numero} falhou: ${err.message}`);
        onRecarregar?.();
        return;
      }
    }
    setDespachando(false);
    onRecarregar?.();
  }

  return (
    <div style={{
      background: hovered ? t.cardHover : t.cardBg,
      borderRadius: '10px',
      borderLeft: `4px solid ${tudoPronto ? corPronto : cor}`,
      transition: 'background 0.15s, border-color 0.2s',
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      <div
        onClick={() => setAberto(v => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          padding: '14px 18px',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem', fontWeight: 700, color: '#a78bfa' }}>
              O.S {grupo.ordemNumero}
            </span>
            {tudoPronto && (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={corPronto} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
          </div>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {grupo.cliente}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 700,
              color: cor, background: cor + '18', border: `1px solid ${cor}40`,
              borderRadius: '20px', padding: '2px 10px', whiteSpace: 'nowrap',
            }}>
              {total} {total === 1 ? 'item' : 'itens'}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textSecondary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: aberto ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>

          {despachando ? (
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', fontWeight: 700, color: corPronto, letterSpacing: '0.03em' }}>
              Despachando {progresso?.feitos}/{progresso?.total}...
            </span>
          ) : confirmando ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.66rem', color: t.textSecondary, whiteSpace: 'nowrap' }}>
                Despachar {total}?
              </span>
              <button
                onClick={despacharTudo}
                style={{
                  background: corPronto, color: '#0a0a0a', border: 'none', borderRadius: '5px',
                  padding: '2px 8px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Sim
              </button>
              <button
                onClick={() => setConfirmando(false)}
                style={{
                  background: 'transparent', color: t.textSecondary, border: `1px solid ${t.colBorder}`,
                  borderRadius: '5px', padding: '2px 8px', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Não
              </button>
            </div>
          ) : tudoPronto ? (
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmando(true); }}
              style={{
                background: corPronto + '18', color: corPronto, border: `1px solid ${corPronto}55`,
                borderRadius: '20px', padding: '2px 10px', fontSize: '0.68rem', fontWeight: 700,
                letterSpacing: '0.03em', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
              }}
            >
              PRONTO · Despachar
            </button>
          ) : (
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', fontWeight: 700,
                color: t.textSecondary, letterSpacing: '0.03em', textAlign: 'right',
              }}
            >
              {faltamChegar > 0
                ? `${faltamChegar} ${faltamChegar === 1 ? 'item não chegou' : 'itens não chegaram'}`
                : `${prontos}/${total} prontos`}
            </span>
          )}
        </div>
      </div>

      {erroDespacho && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            margin: '0 14px 12px', padding: '8px 10px', borderRadius: '7px',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          }}
        >
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: '#ef4444', margin: 0, lineHeight: 1.4 }}>
            {erroDespacho}
          </p>
          <button
            onClick={() => setErroDespacho('')}
            style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.68rem', textDecoration: 'underline', cursor: 'pointer', padding: '4px 0 0', fontFamily: "'DM Sans', sans-serif" }}
          >
            Fechar
          </button>
        </div>
      )}

      {aberto && (
        <div style={{ padding: '0 10px 10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {grupo.itens.map((item) => {
            const pronto = itemEstaPronto(item);
            return (
              <div
                key={item.id}
                onClick={(e) => { e.stopPropagation(); onAbrirModal(item); }}
                style={{
                  background: t.colBg,
                  borderRadius: '7px', padding: '8px 10px',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  cursor: 'pointer',
                }}
              >
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                  background: pronto ? corPronto : '#ef4444',
                }} title={pronto ? 'Checklist completo' : `${item.processos_concluidos}/${item.processos_total} processos concluídos`} />
                {item.dataEntrega && (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', color: t.textCode, fontWeight: 700, flexShrink: 0 }}>
                    {dataEntregaNoFormatoBrasileiro(item.dataEntrega)}
                  </span>
                )}
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {item.descricao}
                </span>
                {typeof item.qtde === 'number' && (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', color: t.textSecondary, flexShrink: 0 }}>
                    {item.qtde} un
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COLUNA DE SETOR
// Representa uma coluna inteira do Kanban (cabeçalho + lista de itens).
// Props:
//   • setor       → nome do setor (string)
//   • itens       → array de itens neste setor
//   • t           → objeto de tema
//   • onAbrirModal → função para abrir o modal ao clicar num cartão
// ─────────────────────────────────────────────────────────────────────────────
function ColunaSetor({ setor, itens, t, onAbrirModal, mostrarDetalhes, onRecarregar, pendentesPorOrdem }) {
  const cor = COR_SETOR[setor] || '#888';
  const isArteFinalista = setor === 'Arte Finalista';
  const [abaResponsavel, setAbaResponsavel] = useState('Luiz');

  const itensSemResponsavel = isArteFinalista ? itens.filter(i => !i.arte_finalista_responsavel) : [];
  const itensFiltrados = !isArteFinalista ? itens : itens.filter(i =>
    !i.arte_finalista_responsavel || i.arte_finalista_responsavel === abaResponsavel
  );

  // PROTÓTIPO: agrupa por O.S só na coluna Expedição, pra reduzir a quantidade de cards
  const isExpedicao = setor === 'Expedição';
  const gruposExpedicao = isExpedicao
    ? Object.values(
        itensFiltrados.reduce((acc, item) => {
          const chave = item.ordemNumero;
          if (!acc[chave]) acc[chave] = { ordemNumero: item.ordemNumero, cliente: item.cliente, itens: [] };
          acc[chave].itens.push(item);
          return acc;
        }, {})
      )
    : null;

  return (
    <div className="kanban-col" style={{
      flexShrink: 0,
      width: '300px',
      display: 'flex',
      flexDirection: 'column',
      background: t.colBg,
      borderRadius: '14px',
      overflow: 'hidden',
      border: `1px solid ${t.colBorder}`,
    }}>

      <div style={{
        borderTop: `4px solid ${cor}`,
        padding: '14px 16px',
        borderBottom: `1px solid ${t.colHeaderBorder}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isArteFinalista ? '10px' : 0 }}>
          <h2 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.25rem', letterSpacing: '0.08em', color: t.textHeading, margin: 0 }}>
            {setor}
          </h2>
          <span style={{
            background: cor + '22', color: cor,
            borderRadius: '20px', padding: '3px 12px',
            fontSize: '0.9rem', fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {itens.length}
          </span>
        </div>

        {isArteFinalista && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {['Luiz', 'Rafael'].map((nome) => {
              const ativo = abaResponsavel === nome;
              const count = itens.filter(i => i.arte_finalista_responsavel === nome).length;
              const corAtendimento = COR_SETOR['Atendimento'];
              return (
                <button
                  key={nome}
                  onClick={() => setAbaResponsavel(nome)}
                  aria-pressed={ativo}
                  style={{
                    background: ativo ? corAtendimento + '22' : corAtendimento + '0d',
                    border: `1px solid ${ativo ? corAtendimento + '88' : corAtendimento + '44'}`,
                    borderRadius: '6px',
                    padding: '3px 8px',
                    color: corAtendimento,
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '0.7rem',
                    fontWeight: ativo ? 700 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {nome} <span style={{ opacity: 0.75 }}>({count})</span>
                </button>
              );
            })}
            {itensSemResponsavel.length > 0 && (
              <span style={{ color: t.textSecondary, fontFamily: "'DM Sans', sans-serif", fontSize: '0.66rem', alignSelf: 'center' }}>
                {itensSemResponsavel.length} sem responsável
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{
        flex: 1,
        padding: '12px',
        display: 'flex', flexDirection: 'column', gap: '9px',
        justifyContent: itensFiltrados.length === 0 ? 'center' : 'flex-start',
        minHeight: '160px',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 160px)',
      }}>
        {itensFiltrados.length === 0
          ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', opacity: 0.35 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="3"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="13" x2="13" y2="13"/>
              </svg>
              <p style={{ color: t.textHeading, fontSize: '0.78rem', textAlign: 'center', margin: 0, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.04em' }}>Nenhum item</p>
            </div>
          )
          : isExpedicao
          ? gruposExpedicao.map((grupo) => <CartaoOrdemAgrupada key={grupo.ordemNumero} grupo={grupo} cor={cor} t={t} onAbrirModal={onAbrirModal} onRecarregar={onRecarregar} pendentes={pendentesPorOrdem[grupo.ordemNumero] || []} />)
          : itensFiltrados.map((item) => <CartaoItem key={item.id} item={item} cor={cor} t={t} onClick={onAbrirModal} mostrarDetalhes={mostrarDetalhes} />)
        }
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOADING SKELETON
// Exibido enquanto os dados ainda estão sendo buscados na API.
// Mostra colunas "fantasma" com a mesma largura das reais para evitar
// a tela "piscar" de vazio para preenchida.
// ─────────────────────────────────────────────────────────────────────────────

function hojeEmBrasiliaISO() {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const valores = Object.fromEntries(partes.map(({ type, value }) => [type, value]));
  return `${valores.year}-${valores.month}-${valores.day}`;
}

function dataEntregaNoFormatoBrasileiro(valor) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : valor;
}

function NotificacaoNovasOS({ notificacao, onFechar, onDataSalva }) {
  const [datas, setDatas] = useState({});
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(null);
  const [avisoFechamento, setAvisoFechamento] = useState('');
  const [osAberta, setOsAberta] = useState(null);

  useEffect(() => {
    if (!notificacao.visivel) setOsAberta(null);
  }, [notificacao.visivel]);

  function tentarFechar() {
    setAvisoFechamento('');
    onFechar();
  }

  async function salvarData(item) {
    const texto = datas[item.numero] || '';
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto);
    const dataISO = match ? `${match[3]}-${match[2]}-${match[1]}` : '';
    const data = dataISO ? new Date(`${dataISO}T00:00:00.000Z`) : null;
    const hoje = hojeEmBrasiliaISO();
    if (!data || Number.isNaN(data.getTime()) || data.toISOString().slice(0, 10) !== dataISO || dataISO < hoje) {
      setErros((atual) => ({ ...atual, [item.numero]: `Coloque uma data igual ou posterior a ${dataEntregaNoFormatoBrasileiro(hoje)}.` }));
      return;
    }

    setSalvando(item.numero);
    setErros((atual) => ({ ...atual, [item.numero]: '' }));
    try {
      await api.liberarDataEntrega(item.numero, dataISO);
      await onDataSalva(item.numero);
      setAvisoFechamento('');
    } catch (err) {
      setErros((atual) => ({ ...atual, [item.numero]: err.message }));
    } finally {
      setSalvando(null);
    }
  }

  if (!notificacao.visivel) return null;
  const { ordens } = notificacao;
  const grupos = Object.values(ordens.reduce((acumulado, ordem) => {
    const numeroOS = ordem.ordem_numero || ordem.numero;
    if (!acumulado[numeroOS]) acumulado[numeroOS] = { numero: numeroOS, cliente: ordem.cliente, itens: [] };
    if (ordem.aguardaData) acumulado[numeroOS].itens.push(ordem);
    return acumulado;
  }, {}));
  const grupoAberto = grupos.find((grupo) => grupo.numero === osAberta);
  const gruposPendentes = grupos.filter((grupo) => grupo.itens.length > 0);
  return (
    <div
      onClick={tentarFechar}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); tentarFechar(); } }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.5rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="toast-entrar"
        style={{
          background: '#141414',
          border: '1px solid #2a2a2a',
          borderTop: '4px solid #00c9db',
          borderRadius: '16px',
          width: '100%', maxWidth: '500px',
          maxHeight: '72vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.75)',
        }}
      >
        {/* Cabeçalho */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.25rem 1.5rem', flexShrink: 0,
          borderBottom: '1px solid #222',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#00c9db', flexShrink: 0, display: 'inline-block' }} />
            <span style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.25rem', color: '#00c9db', letterSpacing: '0.08em' }}>
              {gruposPendentes.length > 0
                ? `${gruposPendentes.length} O.S. aguardando datas`
                : grupos.length === 1 ? 'Nova Ordem de Serviço' : `${grupos.length} Novas Ordens de Serviço`}
            </span>
          </div>
          <button
            onClick={tentarFechar}
            aria-label="Fechar aviso de novas O.S."
            style={{ background: 'transparent', border: 'none', color: '#555', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 1rem', flexShrink: 0 }}
          >×</button>
        </div>

        {avisoFechamento && (
          <p role="alert" style={{ margin: '0', padding: '0.75rem 1.5rem', color: '#ff7777', background: 'rgba(224,51,51,0.1)', borderBottom: '1px solid rgba(224,51,51,0.25)', fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem' }}>
            {avisoFechamento}
          </p>
        )}

        {/* Lista de O.S. e itens da O.S. selecionada */}
        <div style={{ overflowY: 'auto', padding: '0.5rem 0' }}>
          {grupoAberto && (
            <button
              type="button"
              onClick={() => { setOsAberta(null); setAvisoFechamento(''); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem 1.5rem', background: 'transparent', border: 'none', borderBottom: '1px solid #222', color: '#00c9db', fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', cursor: 'pointer' }}
            >
              ← Voltar às O.S.
            </button>
          )}
          {!grupoAberto && grupos.map((grupo, i) => (
            <button
              key={grupo.numero}
              type="button"
              onClick={() => { if (grupo.itens.length) { setOsAberta(grupo.numero); setAvisoFechamento(''); } }}
              disabled={!grupo.itens.length}
              style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%', textAlign: 'left', padding: '1rem 1.5rem', background: 'transparent', border: 'none', borderBottom: i < grupos.length - 1 ? '1px solid #262626' : 'none', cursor: grupo.itens.length ? 'pointer' : 'default' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1rem', color: '#f0a500', fontWeight: 700 }}>O.S {grupo.numero}</span>
                {grupo.itens.length > 0 && <span style={{ color: '#00c9db', fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem' }}>{grupo.itens.length} {grupo.itens.length === 1 ? 'item pendente' : 'itens pendentes'} ›</span>}
              </span>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: '#bbb' }}>{grupo.cliente}</span>
            </button>
          ))}
          {grupoAberto && (
            <div style={{ padding: '0.85rem 1.5rem 0.35rem' }}>
              <span style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: '1rem', color: '#f0a500', fontWeight: 700 }}>O.S {grupoAberto.numero}</span>
              <span style={{ display: 'block', marginTop: '4px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#bbb' }}>{grupoAberto.cliente} · {grupoAberto.itens.length} {grupoAberto.itens.length === 1 ? 'item sem data' : 'itens sem data'}</span>
            </div>
          )}
          {grupoAberto?.itens.map((o, i) => (
            <div
              key={o.numero}
              style={{
                display: 'flex', flexDirection: 'column', gap: '5px',
                padding: '0.9rem 1.5rem',
                borderBottom: i < grupoAberto.itens.length - 1 ? '1px solid #262626' : 'none',
              }}
            >
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.86rem', color: '#e0e0e0' }}>
                Item {o.numero} · {o.descricao} {o.qtde ? `(${o.qtde} un)` : ''}
              </span>
              {o.aguardaData && (
                <form onSubmit={(e) => { e.preventDefault(); salvarData(o); }} style={{ marginTop: '10px' }}>
                  <label htmlFor={`data-entrega-${o.numero}`} style={{ display: 'block', color: '#ccc', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', marginBottom: '6px' }}>
                    Data de entrega deste item
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      id={`data-entrega-${o.numero}`}
                      type="text" inputMode="numeric" placeholder="DD/MM/AAAA" maxLength={10}
                      value={datas[o.numero] || ''}
                      onChange={(e) => {
                        const numeros = e.target.value.replace(/\D/g, '').slice(0, 8);
                        const mascarado = numeros.length > 4 ? `${numeros.slice(0, 2)}/${numeros.slice(2, 4)}/${numeros.slice(4)}` : numeros.length > 2 ? `${numeros.slice(0, 2)}/${numeros.slice(2)}` : numeros;
                        setDatas((atual) => ({ ...atual, [o.numero]: mascarado }));
                        setErros((atual) => ({ ...atual, [o.numero]: '' }));
                        setAvisoFechamento('');
                      }}
                      style={{ flex: '1 1 160px', minWidth: 0, background: '#1b1b1b', border: '1px solid #555', borderRadius: '8px', color: '#f0f0f0', padding: '9px 12px', fontSize: '0.9rem' }}
                    />
                    <button type="submit" disabled={salvando === o.numero} style={{ background: '#00c9db', border: 'none', borderRadius: '8px', color: '#061618', padding: '9px 14px', fontWeight: 700, cursor: salvando === o.numero ? 'wait' : 'pointer' }}>
                      {salvando === o.numero ? 'Salvando...' : 'Salvar data'}
                    </button>
                  </div>
                  {erros[o.numero] && <p role="alert" style={{ color: '#ff7777', fontSize: '0.78rem', margin: '7px 0 0' }}>{erros[o.numero]}</p>}
                </form>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PopupItensReconciliados({ itens, onFechar }) {
  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.5rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="toast-entrar"
        style={{
          background: '#141414',
          border: '1px solid #2a2a2a',
          borderTop: '4px solid #00c9db',
          borderRadius: '16px',
          width: '100%', maxWidth: '500px',
          maxHeight: '72vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.75)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.25rem 1.5rem', flexShrink: 0,
          borderBottom: '1px solid #222',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#00c9db', flexShrink: 0, display: 'inline-block' }} />
            <span style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.25rem', color: '#00c9db', letterSpacing: '0.08em' }}>
              Itens novos encontrados
            </span>
          </div>
          <button
            onClick={onFechar}
            style={{ background: 'transparent', border: 'none', color: '#555', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 1rem', flexShrink: 0 }}
          >×</button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0.5rem 0' }}>
          {itens.map((item, i) => (
            <div
              key={item.numero}
              style={{
                display: 'flex', flexDirection: 'column', gap: '5px',
                padding: '0.9rem 1.5rem',
                borderBottom: i < itens.length - 1 ? '1px solid #1e1e1e' : 'none',
              }}
            >
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1rem', color: '#f0a500', fontWeight: 700 }}>
                {item.numero}
              </span>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: '#bbb' }}>
                {item.descricao}
              </span>
              {item.renomeado && (
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: '#888' }}>
                  Número ajustado automaticamente — o PDV reaproveitou {item.numeroOriginal}.
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModalReconciliarOS({ onClose, numeroInicial }) {
  const [numero, setNumero] = useState(numeroInicial || '');
  const [reconciliando, setReconciliando] = useState(false);
  const [erro, setErro] = useState('');
  const [semNovidade, setSemNovidade] = useState(false);
  const [itensReconciliados, setItensReconciliados] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  async function reconciliar(e) {
    e.preventDefault();
    if (!numero.trim()) return;
    setReconciliando(true);
    setErro('');
    setSemNovidade(false);
    try {
      const { itensNovos } = await api.reconciliarOrdem(numero.trim());
      setNumero('');
      if (itensNovos.length > 0) {
        setItensReconciliados(itensNovos);
      } else {
        setSemNovidade(true);
      }
    } catch (err) {
      setErro(err.message);
    } finally {
      setReconciliando(false);
    }
  }

  const inputStyle = {
    flex: 1, background: '#181818', border: '1px solid #2a2a2a',
    borderRadius: '10px', padding: '0.75rem 1rem', color: '#e8e8e8',
    fontSize: '1rem', fontFamily: "'JetBrains Mono', monospace", outline: 'none',
    transition: 'border-color 0.18s',
  };

  return (
    <>
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', backdropFilter: 'blur(4px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#141414', border: '1px solid #2a2a2a', borderTop: '4px solid #00c9db', borderRadius: '16px', width: '100%', maxWidth: '540px', fontFamily: "'DM Sans', sans-serif" }}
      >
        <div style={{ padding: '1.75rem' }}>

          {/* Cabeçalho */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h2 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.5rem', color: '#e0e0e0', letterSpacing: '0.08em', margin: 0 }}>
              RECONCILIAR ORDEM DE SERVIÇO
            </h2>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#555', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 1rem', flexShrink: 0 }}>×</button>
          </div>

          {/* Formulário de reconciliação */}
          <form onSubmit={reconciliar} style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
            <input
              ref={inputRef}
              value={numero}
              onChange={(e) => { setNumero(e.target.value); setErro(''); setSemNovidade(false); }}
              placeholder="Número da O.S..."
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#00c9db'}
              onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
            />
            <button
              type="submit"
              disabled={reconciliando}
              style={{ background: '#00c9db', border: 'none', borderRadius: '10px', padding: '0.75rem 1.25rem', color: '#0d0d0d', fontFamily: "'Bebas Neue', cursive", fontSize: '1.15rem', letterSpacing: '0.08em', cursor: reconciliando ? 'not-allowed' : 'pointer', flexShrink: 0 }}
            >
              {reconciliando ? '...' : 'RECONCILIAR'}
            </button>
          </form>

          {/* Mensagem de erro */}
          {erro && (
            <div style={{ background: 'rgba(224,51,51,0.07)', border: '1px solid rgba(224,51,51,0.2)', borderRadius: '10px', padding: '0.85rem 1rem' }}>
              <p style={{ color: '#e05555', fontSize: '0.9rem', margin: 0 }}>{erro}</p>
            </div>
          )}

          {/* Mensagem neutra quando não há item novo */}
          {semNovidade && (
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: 0, textAlign: 'center' }}>
              Nenhum item novo encontrado.
            </p>
          )}
        </div>
      </div>
    </div>
    {itensReconciliados && (
      <PopupItensReconciliados
        itens={itensReconciliados}
        onFechar={() => setItensReconciliados(null)}
      />
    )}
    </>
  );
}

function Loading({ t, setoresVisiveis = SETORES }) {
  return (
    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
      {/* Cria uma coluna fantasma para cada setor, opacidade 50% */}
      {setoresVisiveis.map((s) => (
        <div key={s} className="skeleton-shimmer" style={{ flexShrink: 0, width: '300px', height: '340px', borderRadius: '14px', border: `1px solid ${t.colBorder}` }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL DE ITEM
// Janela flutuante exibida ao clicar em um cartão ou ao tentar bipar SAÍDA
// sem processos completos. Contém:
//   1. Banner de aviso âmbar (só aparece quando "aviso" é passado)
//   2. Cabeçalho com número, descrição e setor do item
//   3. Checklist interativa de processos do setor atual
//   4. Seção de retrocesso de setor (se não for o primeiro)
//
// Props:
//   • item        → objeto do item selecionado
//   • t           → objeto de tema
//   • onClose     → fecha o modal
//   • onRetrograde → chamado após retroceder (atualiza o Kanban)
//   • aviso       → string de aviso (vazia = sem banner)
// ─────────────────────────────────────────────────────────────────────────────
function ModalItem({ item, t, onClose, onRetrograde, onBiparSucesso, aviso }) {
  const [processos, setProcessos]           = useState([]);
  const [checklist, setChecklist]           = useState([]);
  const [anotacoes, setAnotacoes]               = useState([]);
  const [novaAnotacao, setNovaAnotacao]         = useState('');
  const [adicionandoAnot, setAdicionandoAnot]   = useState(false);
  const [carregandoProc, setCarregandoProc] = useState(true);
  const [confirmando, setConfirmando]         = useState(false);
  const [motivoRetro, setMotivoRetro]         = useState('');
  const [carregandoRetro, setCarregandoRetro] = useState(false);
  const [erroRetro, setErroRetro]           = useState('');
  const [erroProcesso, setErroProcesso]     = useState('');
  const [confirmandoSaida, setConfirmandoSaida] = useState(false);
  const [escolhendoTipo, setEscolhendoTipo]               = useState(false);
  const [tipoSelecionado, setTipoSelecionado]             = useState(null);
  const [destinoDireto, setDestinoDireto]                 = useState(null);
  const [escolhendoSetorAtendimento, setEscolhendoSetorAtendimento] = useState(false);
  const [setorAtendimentoSelecionado, setSetorAtendimentoSelecionado] = useState(null);
  const [escolhendoSetorAcabamento, setEscolhendoSetorAcabamento] = useState(false);
  const [setorAcabamentoSelecionado, setSetorAcabamentoSelecionado] = useState(null);
  const [escolhendoProximoSetorFora, setEscolhendoProximoSetorFora] = useState(false);
  const [setorForaSelecionado, setSetorForaSelecionado] = useState(null);
  const [escolhendoSetorCorte, setEscolhendoSetorCorte] = useState(false);
  const [setorCorteSelecionado, setSetorCorteSelecionado] = useState(null);
  const [escolhendoSetorImpressaoDigital, setEscolhendoSetorImpressaoDigital] = useState(false);
  const [setorImpressaoDigitalSelecionado, setSetorImpressaoDigitalSelecionado] = useState(null);
  const [escolhendoResponsavel, setEscolhendoResponsavel] = useState(false);
  const [responsavelSelecionado, setResponsavelSelecionado] = useState(null);
  const [bipando, setBipando]               = useState(false);
  const [erroBipar, setErroBipar]           = useState('');
  const [entradaEm, setEntradaEm]           = useState(item.entrada_em);
  const [chapaBuscada, setChapaBuscada]     = useState(item.chapa_buscada || false);
  const [buscandoChapa, setBuscandoChapa]   = useState(false);
  const [erroChapa, setErroChapa]           = useState('');
  const [cartelaConfirmada, setCartelaConfirmada] = useState(item.cartela_confirmada || false);
  const [confirmandoCartela, setConfirmandoCartela] = useState(false);
  const [loadingCartela, setLoadingCartela]       = useState(false);
  const [erroCartela, setErroCartela]             = useState('');
  const usuarioLogado = JSON.parse(localStorage.getItem('usuario') || '{}');
  const setoresAcessoUsuario = usuarioLogado.setores_acesso || [];
  const podeEditar = usuarioLogado.perfil === 'admin'
    || usuarioLogado.acesso_total
    || usuarioLogado.setor === item.setor_atual
    || setoresAcessoUsuario.includes(item.setor_atual);
  const podeAnotar  = !!usuarioLogado.id;

  // Normaliza o número da ordem (pode vir como ordemNumero ou ordem_numero
  // dependendo se o item veio do Kanban ou do painel de bipagem)
  const ordemNum    = item.ordemNumero || item.ordem_numero;
  const setorAnterior = getSetorAnterior(item.setor_atual);
  const cor           = COR_SETOR[item.setor_atual] || '#888';
  const corAnterior   = setorAnterior ? COR_SETOR[setorAnterior] : '#888';

  // Derivações calculadas a partir dos estados — não precisam de useState próprio
  const concluidos      = checklist.filter(Boolean).length;
  // Considera "todos concluídos" se não há processos OU se todos foram marcados
  const todosConcluidos = processos.length === 0 || concluidos === processos.length;

  // Auto-dismiss de 5s para erros do modal
  useEffect(() => {
    if (!erroProcesso) return;
    const t = setTimeout(() => setErroProcesso(''), 5000);
    return () => clearTimeout(t);
  }, [erroProcesso]);

  useEffect(() => {
    if (!erroRetro) return;
    const t = setTimeout(() => setErroRetro(''), 5000);
    return () => clearTimeout(t);
  }, [erroRetro]);

  // ── Busca os processos do item ao abrir o modal ──────────────────────────
  useEffect(() => {
    Promise.all([
      api.getProcessos(item.numero),
      api.getAnotacoes(item.numero),
    ]).then(([procData, anotData]) => {
      setProcessos(procData.processos.map((p) => p.processo));
      setChecklist(procData.processos.map((p) => p.concluido));
      setAnotacoes(anotData);
    }).finally(() => setCarregandoProc(false));
  }, [item.numero]);

  // ── Registrar entrada ────────────────────────────────────────────────────
  async function biparEntrada() {
    setBipando(true);
    setErroBipar('');
    try {
      await api.bipar(item.numero, 'entrada');
      setEntradaEm(new Date().toISOString());
      onBiparSucesso?.();
    } catch (err) {
      setErroBipar(err.message);
    } finally {
      setBipando(false);
    }
  }

  // ── Buscar chapa (Pré Produção) ─────────────────────────────────────────
  async function executarBuscarChapa() {
    setBuscandoChapa(true);
    setErroChapa('');
    try {
      await api.buscarChapa(item.numero);
      setChapaBuscada(true);
      onBiparSucesso?.();
    } catch (err) {
      setErroChapa(err.message);
    } finally {
      setBuscandoChapa(false);
    }
  }

  // ── Confirmar / cancelar cartela (Arte Finalista) ───────────────────────
  async function executarConfirmarCartela() {
    setLoadingCartela(true);
    setErroCartela('');
    try {
      await api.confirmarCartela(item.numero);
      setCartelaConfirmada(true);
      setConfirmandoCartela(false);
      onBiparSucesso?.();
    } catch (err) {
      setErroCartela(err.message);
    } finally {
      setLoadingCartela(false);
    }
  }

  async function executarCancelarCartela() {
    setLoadingCartela(true);
    setErroCartela('');
    try {
      await api.cancelarCartela(item.numero);
      setCartelaConfirmada(false);
      onBiparSucesso?.();
    } catch (err) {
      setErroCartela(err.message);
    } finally {
      setLoadingCartela(false);
    }
  }

  // ── Toggle de processo (atualização otimista) ────────────────────────────
  async function toggleProcesso(i) {
    if (!entradaEm) return;
    if (item.setor_atual === 'Pré Produção' && !chapaBuscada) return;
    if (!podeEditar) {
      setErroProcesso(`Você está no setor "${usuarioLogado.setor}". Só é possível editar processos do seu próprio setor.`);
      return;
    }
    setErroProcesso('');
    const novo = !checklist[i];
    setChecklist((prev) => prev.map((v, idx) => idx === i ? novo : v));
    try {
      await api.atualizarProcesso(item.numero, processos[i], novo);
      // Sem isso, o card agrupado da O.S (selo PRONTO, bolinhas) só via a
      // mudança no próximo polling de 30s — o modal atualizava na hora, o
      // resto do board não.
      onBiparSucesso?.();
    } catch (err) {
      setChecklist((prev) => prev.map((v, idx) => idx === i ? !novo : v));
      setErroProcesso(err.message);
    }
  }

  async function enviarAnotacao() {
    if (!novaAnotacao.trim()) return;
    setAdicionandoAnot(true);
    try {
      const nova = await api.adicionarAnotacao(item.numero, novaAnotacao);
      setAnotacoes((prev) => [...prev, nova]);
      setNovaAnotacao('');
    } catch (err) {
      alert(err.message);
    } finally {
      setAdicionandoAnot(false);
    }
  }

  // ── Confirmar retrocesso ─────────────────────────────────────────────────
  async function confirmarRetrograde() {
    setCarregandoRetro(true);
    setErroRetro('');
    try {
      const resp = await api.retrograde(item.numero, motivoRetro);
      if (resp.anotacao) setAnotacoes((prev) => [...prev, resp.anotacao]);
      await onRetrograde();
      onClose();
    } catch (err) {
      setErroRetro(err.message);
      setCarregandoRetro(false);
    }
  }

  // ── Excluir anotação ─────────────────────────────────────────────────────
  async function excluirAnotacao(id) {
    try {
      await api.deletarAnotacao(item.numero, id);
      setAnotacoes((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      alert(err.message);
    }
  }

  async function biparSaida() {
    setBipando(true);
    setErroBipar('');
    try {
      await api.bipar(item.numero, 'saida', tipoSelecionado, null, setorAtendimentoSelecionado, null, null, setorAcabamentoSelecionado, setorForaSelecionado, destinoDireto, setorCorteSelecionado, setorImpressaoDigitalSelecionado, responsavelSelecionado);
      onBiparSucesso?.();
      onClose();
    } catch (err) {
      setErroBipar(err.message);
      setBipando(false);
    }
  }

  return (
    // Fundo escurecido (overlay): cobre toda a tela e fecha o modal ao clicar
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, // inset: 0 é atalho para top/right/bottom/left: 0
        zIndex: 1000,                 // fica acima de tudo na página
        background: 'rgba(0,0,0,0.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.5rem',
        backdropFilter: 'blur(4px)', // desfoca o conteúdo atrás do overlay
      }}
    >
      {/* Caixa do modal: stopPropagation impede que o clique interno feche o modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#141414',
          border: '1px solid #2a2a2a',
          borderTop: `4px solid ${cor}`,  // faixa colorida do setor no topo
          borderRadius: '16px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',              // scroll se o conteúdo for maior que 90% da tela
          fontFamily: "'DM Sans', sans-serif",
        }}
      >

        {/* ── Banner de aviso âmbar ─────────────────────────────────────────
            Aparece somente quando o modal é aberto via botão SAÍDA com
            processos incompletos. A prop "aviso" é uma string com a mensagem.
            String vazia = falsy em JS = bloco não renderizado. */}
        {aviso && (
          <div style={{
            background: 'rgba(240,165,0,0.10)',
            borderBottom: '1px solid rgba(240,165,0,0.25)',
            padding: '0.9rem 1.5rem',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>⚠️</span>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: '#f0a500', margin: 0, lineHeight: 1.4 }}>
              {aviso}
            </p>
          </div>
        )}

        {item.setor_atual === 'Concluído' ? (
          <div style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid rgba(74,222,128,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{
              background: 'rgba(74,222,128,0.12)',
              border: '1.5px solid rgba(74,222,128,0.45)',
              borderRadius: '999px',
              padding: '0.45rem 1.4rem',
              color: '#4ade80',
              fontFamily: "'Bebas Neue', cursive",
              fontSize: '1.15rem',
              letterSpacing: '0.1em',
            }}>
              ✓ Concluído
            </span>
          </div>
        ) : !entradaEm && (
          <div style={{
            background: 'rgba(74,222,128,0.06)',
            borderBottom: '1px solid rgba(74,222,128,0.18)',
            padding: '1rem 1.5rem',
            display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: '#a0a0a0', margin: 0, lineHeight: 1.4 }}>
              Entrada não registrada neste setor.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#f0a500', fontWeight: 700 }}>
                Aguardando Entrada
              </span>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: '#f0a500', fontWeight: 600 }}>
                {formatarTempo(item.criado_em)}
              </span>
            </div>
            {erroBipar && (
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#e05555', margin: 0 }}>{erroBipar}</p>
            )}
            <button
              onClick={biparEntrada}
              disabled={bipando}
              style={{
                background: bipando ? '#1a2a1a' : '#1a2a1a',
                border: `1px solid ${bipando ? '#2a4a2a' : '#4ade80'}`,
                borderRadius: '10px', padding: '0.75rem 0',
                color: '#4ade80', fontFamily: "'Bebas Neue', cursive",
                fontSize: '1.4rem', letterSpacing: '0.08em',
                cursor: bipando ? 'not-allowed' : 'pointer',
                opacity: bipando ? 0.6 : 1,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { if (!bipando) e.currentTarget.style.background = '#1f361f'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#1a2a1a'; }}
            >
              {bipando ? 'Registrando...' : 'DAR ENTRADA'}
            </button>
          </div>
        )}

        {/* ── Cartela Pronta (Arte Finalista com cartela) ────────────────── */}
        {entradaEm && item.setor_atual === 'Arte Finalista' && item.precisa_cartela && !cartelaConfirmada && (
          <div style={{
            background: 'rgba(167,139,250,0.06)',
            borderBottom: '1px solid rgba(167,139,250,0.22)',
            padding: '1rem 1.5rem',
            display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: '#a78bfa', margin: 0, lineHeight: 1.4 }}>
              Aguardando componentes da cartela.
            </p>
            {erroCartela && (
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#e05555', margin: 0 }}>{erroCartela}</p>
            )}
            {confirmandoCartela ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#e0e0e0', fontWeight: 600, margin: 0 }}>
                  Confirmar cartela pronta?
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button
                    onClick={() => setConfirmandoCartela(false)}
                    style={{
                      background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '8px',
                      padding: '0.65rem 0', color: '#888',
                      fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em',
                      cursor: 'pointer',
                    }}
                  >
                    NÃO
                  </button>
                  <button
                    onClick={executarConfirmarCartela}
                    disabled={loadingCartela}
                    style={{
                      background: '#1a1228', border: '1px solid #a78bfa',
                      borderRadius: '8px', padding: '0.65rem 0',
                      color: '#a78bfa', fontFamily: "'Bebas Neue', cursive",
                      fontSize: '1.1rem', letterSpacing: '0.08em',
                      cursor: loadingCartela ? 'not-allowed' : 'pointer',
                      opacity: loadingCartela ? 0.6 : 1,
                    }}
                  >
                    {loadingCartela ? 'AGUARDE...' : 'SIM'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmandoCartela(true)}
                style={{
                  background: '#1a1228', border: '1px solid #a78bfa',
                  borderRadius: '10px', padding: '0.75rem 0',
                  color: '#a78bfa', fontFamily: "'Bebas Neue', cursive",
                  fontSize: '1.4rem', letterSpacing: '0.08em',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#221840'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#1a1228'; }}
              >
                CARTELA PRONTA
              </button>
            )}
          </div>
        )}

        {entradaEm && item.setor_atual === 'Arte Finalista' && item.precisa_cartela && cartelaConfirmada && (
          <div style={{
            background: 'rgba(167,139,250,0.06)',
            borderBottom: '1px solid rgba(167,139,250,0.18)',
            padding: '0.75rem 1.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#a78bfa', fontSize: '0.95rem' }}>✓</span>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#a78bfa', fontWeight: 600 }}>
                Cartela confirmada
              </span>
            </div>
            {erroCartela && (
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: '#e05555' }}>{erroCartela}</span>
            )}
            <button
              onClick={executarCancelarCartela}
              disabled={loadingCartela}
              style={{
                background: loadingCartela ? 'transparent' : 'rgba(167,139,250,0.12)',
                border: `1px solid ${loadingCartela ? 'transparent' : 'rgba(167,139,250,0.4)'}`,
                borderRadius: '6px',
                color: loadingCartela ? '#444' : '#a78bfa',
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 600,
                cursor: loadingCartela ? 'not-allowed' : 'pointer',
                padding: '3px 10px', flexShrink: 0, transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { if (!loadingCartela) e.currentTarget.style.background = 'rgba(167,139,250,0.22)'; }}
              onMouseLeave={(e) => { if (!loadingCartela) e.currentTarget.style.background = 'rgba(167,139,250,0.12)'; }}
            >
              {loadingCartela ? 'aguarde...' : 'desfazer'}
            </button>
          </div>
        )}

        {/* ── Buscar Chapa (exclusivo Pré Produção) ───────────────────────── */}
        {entradaEm && item.setor_atual === 'Pré Produção' && !chapaBuscada && (
          <div style={{
            background: 'rgba(0,201,219,0.06)',
            borderBottom: '1px solid rgba(0,201,219,0.20)',
            padding: '1rem 1.5rem',
            display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: '#a0c4c8', margin: 0, lineHeight: 1.4 }}>
              Pré-impressão: chapa ainda não buscada.
            </p>
            {erroChapa && (
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#e05555', margin: 0 }}>{erroChapa}</p>
            )}
            <button
              onClick={executarBuscarChapa}
              disabled={buscandoChapa}
              style={{
                background: '#0a1e22',
                border: `1px solid ${buscandoChapa ? '#0d3a42' : '#00c9db'}`,
                borderRadius: '10px', padding: '0.75rem 0',
                color: '#00c9db', fontFamily: "'Bebas Neue', cursive",
                fontSize: '1.4rem', letterSpacing: '0.08em',
                cursor: buscandoChapa ? 'not-allowed' : 'pointer',
                opacity: buscandoChapa ? 0.6 : 1,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { if (!buscandoChapa) e.currentTarget.style.background = '#0d2a30'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#0a1e22'; }}
            >
              {buscandoChapa ? 'Registrando...' : 'BUSCAR CHAPA'}
            </button>
          </div>
        )}

        {entradaEm && item.setor_atual === 'Pré Produção' && chapaBuscada && (
          <div style={{
            background: 'rgba(239,68,68,0.04)',
            borderBottom: '1px solid rgba(239,68,68,0.12)',
            padding: '0.65rem 1.5rem',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>✓</span>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#ef4444' }}>
              Chapa buscada
            </span>
          </div>
        )}

        <div style={{ padding: '1.75rem' }}>

          {/* ── Cabeçalho: número, descrição e botão de fechar ─────────────── */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div>
              {/* Número do item em âmbar, fonte monospace */}
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem', color: '#f0a500', fontWeight: 700, margin: 0, marginBottom: '5px' }}>
                {item.numero}
              </p>
              <p style={{ fontSize: '1.1rem', color: '#e0e0e0', fontWeight: 600, margin: 0, lineHeight: 1.35 }}>
                {item.descricao}
              </p>
            </div>
            {/* Botão × para fechar. O caractere × (×) é o "multiplicação" do HTML */}
            <button
              onClick={onClose}
              style={{ background: 'transparent', border: 'none', color: '#555', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 1rem', flexShrink: 0 }}
            >
              ×
            </button>
          </div>

          {/* Badges de setor, número da O.S e cliente */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <span style={{ background: cor + '20', color: cor, borderRadius: '20px', padding: '3px 12px', fontSize: '0.8rem', fontWeight: 700 }}>
              {item.setor_atual}
            </span>
            <span style={{ color: '#555', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
              O.S <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#666' }}>{ordemNum}</span>
            </span>
            {/* React Fragment <>...</> agrupa elementos sem criar um nó DOM extra */}
            {item.cliente && <>
              <span style={{ color: '#333' }}>·</span>
              <span style={{ color: '#555', fontSize: '0.8rem' }}>{item.cliente}</span>
            </>}
          </div>

          {/* ── Tempo no setor ─────────────────────────────────────────────── */}
          {entradaEm && (() => {
            const u = urgencia(entradaEm);
            const corTempo = u?.cor || '#555';
            return (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: corTempo + '10', border: `1px solid ${corTempo}30`,
                borderRadius: '8px', padding: '0.6rem 1rem', marginBottom: '1.5rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={corTempo} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: corTempo, fontWeight: 600 }}>
                    {formatarTempo(entradaEm)} neste setor
                  </span>
                </div>
                {u && (
                  <span className={u.label === 'Alta urgência' ? 'badge-alta-urgencia' : ''} style={{
                    fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700,
                    color: u.cor, background: u.cor + '18', border: `1px solid ${u.cor}40`,
                    borderRadius: '20px', padding: '2px 9px',
                  }}>
                    {u.label}
                  </span>
                )}
              </div>
            );
          })()}

          {/* ── Anotações ───────────────────────────────────────────────────── */}
          <div style={{
            border: '1px solid rgba(74,222,128,0.2)',
            borderLeft: '4px solid #4ade80',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '1.5rem',
          }}>
            {/* Cabeçalho */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: anotacoes.length > 0 ? '12px' : '10px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: '#4ade80', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Anotações {anotacoes.length > 0 && <span style={{ opacity: 0.6 }}>({anotacoes.length})</span>}
              </span>
            </div>

            {/* Lista de anotações */}
            {anotacoes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                {anotacoes.map((anot) => {
                  const isRetrocesso = anot.tipo === 'retrocesso';
                  const cor = isRetrocesso ? '#f87171' : (COR_SETOR[anot.setor] || '#888');
                  const podeExcluir = usuarioLogado.perfil === 'admin' || anot.usuario_id === usuarioLogado.id;
                  return (
                    <div key={anot.id} style={{
                      background: cor + '10',
                      border: `1px solid ${cor}28`,
                      borderLeft: `3px solid ${cor}`,
                      borderRadius: '8px', padding: '8px 11px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '6px', marginBottom: '4px' }}>
                        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', color: cor, margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                          {isRetrocesso && <span style={{ background: cor + '25', borderRadius: '4px', padding: '1px 6px', fontSize: '0.65rem' }}>↩ RETROCESSO</span>}
                          <span>Setor: <span style={{ background: cor + '20', borderRadius: '20px', padding: '1px 8px' }}>{anot.setor}</span></span>
                          <span style={{ fontWeight: 500, opacity: 0.85 }}>Funcionário: {anot.usuario_nome}</span>
                          <span style={{ fontWeight: 400, opacity: 0.5, fontSize: '0.65rem' }}>
                            {new Date(anot.criado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </p>
                        {podeExcluir && (
                          <button
                            onClick={() => excluirAnotacao(anot.id)}
                            title="Excluir anotação"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#444', padding: '0 2px', flexShrink: 0, lineHeight: 1, transition: 'color 0.15s' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#e05555'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#444'}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                            </svg>
                          </button>
                        )}
                      </div>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: isRetrocesso ? '#fca5a5' : '#d0d0d0', margin: 0, lineHeight: 1.45 }}>
                        {anot.texto}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Campo para nova anotação */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <textarea
                placeholder="Adicionar anotação..."
                value={novaAnotacao}
                rows={2}
                onChange={(e) => setNovaAnotacao(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarAnotacao(); } }}
                style={{
                  flex: 1, boxSizing: 'border-box',
                  background: 'rgba(74,222,128,0.04)', border: '1px solid rgba(74,222,128,0.2)',
                  borderRadius: '7px', padding: '8px 10px', outline: 'none', resize: 'none',
                  color: '#e0e0e0', fontSize: '0.88rem', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.4,
                }}
                onFocus={(e) => e.target.style.borderColor = '#4ade80'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(74,222,128,0.2)'}
              />
              <button
                onClick={enviarAnotacao}
                disabled={adicionandoAnot || !novaAnotacao.trim()}
                style={{
                  background: novaAnotacao.trim() ? 'rgba(74,222,128,0.15)' : 'rgba(74,222,128,0.05)',
                  border: '1px solid rgba(74,222,128,0.3)',
                  borderRadius: '7px', padding: '8px 14px',
                  color: '#4ade80', fontSize: '0.78rem',
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
                  cursor: novaAnotacao.trim() ? 'pointer' : 'not-allowed',
                  opacity: adicionandoAnot ? 0.6 : 1,
                  flexShrink: 0, alignSelf: 'stretch',
                  transition: 'all 0.15s',
                }}
              >
                {adicionandoAnot ? '...' : 'Enviar'}
              </button>
            </div>
          </div>

          {/* ── Seção de processos ──────────────────────────────────────────── */}
          <div style={{ borderTop: '1px solid #1e1e1e', paddingTop: '1.25rem', marginBottom: '1.5rem' }}>

            {/* Título da seção + contador X/Y com cor dinâmica */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', color: '#c8c8c8', letterSpacing: '0.08em' }}>
                PROCESSOS DO SETOR
              </span>
              {/* Verde quando tudo está concluído, âmbar quando ainda falta */}
              <span style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: '0.82rem',
                color: todosConcluidos ? '#4ade80' : '#f0a500', fontWeight: 700,
              }}>
                {concluidos}/{processos.length}
              </span>
            </div>

            {/* Barra de progresso: largura calculada em % com base nos concluídos */}
            <div style={{ height: '6px', background: '#1e1e1e', borderRadius: '3px', marginBottom: '14px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                // Percentual: (concluídos / total) * 100, com proteção contra divisão por zero
                width: `${processos.length > 0 ? (concluidos / processos.length) * 100 : 0}%`,
                background: todosConcluidos ? '#4ade80' : '#f0a500',
                borderRadius: '3px',
                transition: 'width 0.3s ease, background 0.3s', // animação suave ao marcar
              }} />
            </div>

            {/* Banner: usuário fora do setor */}
            {!podeEditar && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                background: 'rgba(79,142,247,0.08)', border: '1px solid rgba(79,142,247,0.2)',
                borderRadius: '10px', padding: '12px 14px', marginBottom: '12px',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f8ef7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#4f8ef7', margin: 0, lineHeight: 1.4 }}>
                  Este item está no setor <strong>{item.setor_atual}</strong>. Você só pode editar processos do seu setor (<strong>{usuarioLogado.setor}</strong>).
                </p>
              </div>
            )}

            {/* Mensagem de erro ao tentar editar */}
            {erroProcesso && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                background: 'rgba(224,85,85,0.08)', border: '1px solid rgba(224,85,85,0.2)',
                borderRadius: '10px', padding: '12px 14px', marginBottom: '12px',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e05555" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#e05555', margin: 0, lineHeight: 1.4 }}>
                  {erroProcesso}
                </p>
              </div>
            )}

            {/* Exibe loading enquanto a API não respondeu */}
            {carregandoProc ? (
              <p style={{ color: '#555', fontSize: '0.85rem', textAlign: 'center' }}>Carregando processos...</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {processos.map((proc, i) => (
                  <button
                    key={i}
                    onClick={() => toggleProcesso(i)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      background: checklist[i] ? 'rgba(74,222,128,0.06)' : '#1a1a1a',
                      border: `1px solid ${checklist[i] ? 'rgba(74,222,128,0.25)' : '#252525'}`,
                      borderRadius: '9px', padding: '11px 14px',
                      cursor: podeEditar ? 'pointer' : 'not-allowed',
                      textAlign: 'left', width: '100%',
                      opacity: podeEditar ? 1 : 0.5,
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '6px', flexShrink: 0,
                      border: `2px solid ${checklist[i] ? '#4ade80' : '#333'}`,
                      background: checklist[i] ? '#4ade80' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}>
                      {checklist[i] && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d0d0d" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </div>
                    <span style={{
                      fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem',
                      color: checklist[i] ? '#4ade80' : '#c8c8c8',
                      fontWeight: checklist[i] ? 500 : 400,
                      textDecoration: checklist[i] ? 'line-through' : 'none',
                      opacity: checklist[i] ? 0.7 : 1,
                      transition: 'all 0.15s',
                    }}>
                      {proc}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Seção de SAÍDA ─────────────────────────────────────────────
              Só aparece se o item já tem entrada registrada. */}
          {entradaEm && (
            <div style={{ borderTop: '1px solid #1e1e1e', paddingTop: '1.25rem', marginBottom: '1.5rem' }}>
              {erroBipar && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  background: 'rgba(224,85,85,0.08)', border: '1px solid rgba(224,85,85,0.2)',
                  borderRadius: '10px', padding: '12px 14px', marginBottom: '12px',
                }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#e05555', margin: 0 }}>
                    {erroBipar}
                    {erroBipar.includes('Despacho bloqueado') && (
                      <span style={{ display: 'block', marginTop: '6px', color: '#c04040' }}>
                        Utilize <strong>Buscar O.S</strong> para verificar a localização de cada item.
                      </span>
                    )}
                  </p>
                </div>
              )}
              {escolhendoTipo ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: '0 0 4px 0' }}>
                    Escolha o tipo de impressão:
                  </p>
                  {TIPOS_IMPRESSAO.map((tipo) => {
                    const cor = COR_SETOR[tipo];
                    return (
                      <button
                        key={tipo}
                        onClick={() => {
                          setTipoSelecionado(tipo);
                          setDestinoDireto(null);
                          setEscolhendoTipo(false);
                          setConfirmandoSaida(true);
                        }}
                        style={{
                          background: cor + '18', border: `1px solid ${cor}55`,
                          borderRadius: '9px', padding: '0.75rem 1rem',
                          color: cor, fontFamily: "'Bebas Neue', cursive",
                          fontSize: '1.1rem', letterSpacing: '0.08em',
                          cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                          display: 'flex', flexDirection: 'column', gap: '2px',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = cor + '30'; e.currentTarget.style.borderColor = cor; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = cor + '18'; e.currentTarget.style.borderColor = cor + '55'; }}
                      >
                        <span>{tipo}</span>
                        {tipo === 'Impressão Offset' && (
                          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', letterSpacing: '0.02em', opacity: 0.75 }}>
                            Passa por Pré Produção (busca de chapa)
                          </span>
                        )}
                      </button>
                    );
                  })}
                  <div style={{ borderTop: '1px solid #222', margin: '4px 0' }} />
                  {[
                    { label: 'Para Expedição', dest: 'Expedição' },
                    { label: 'Para Concluído', dest: 'Concluído' },
                  ].map(({ label, dest }) => {
                    const cor = COR_SETOR[dest];
                    return (
                      <button
                        key={dest}
                        onClick={() => {
                          setDestinoDireto(dest);
                          setTipoSelecionado(null);
                          setEscolhendoTipo(false);
                          setConfirmandoSaida(true);
                        }}
                        style={{
                          background: cor + '18', border: `1px solid ${cor}55`,
                          borderRadius: '9px', padding: '0.75rem 1rem',
                          color: cor, fontFamily: "'Bebas Neue', cursive",
                          fontSize: '1.1rem', letterSpacing: '0.08em',
                          cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = cor + '30'; e.currentTarget.style.borderColor = cor; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = cor + '18'; e.currentTarget.style.borderColor = cor + '55'; }}
                      >
                        {label}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setEscolhendoTipo(false)}
                    style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', padding: '0.3rem' }}
                  >
                    Cancelar
                  </button>
                </div>
              ) : escolhendoResponsavel ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: '0 0 4px 0' }}>
                    Quem ficará responsável pela Arte Finalista?
                  </p>
                  {['Luiz', 'Rafael'].map((nome) => {
                    const corResponsavel = nome === 'Luiz' ? '#4ade80' : '#60a5fa';
                    return <button
                      key={nome}
                      onClick={() => {
                        setResponsavelSelecionado(nome);
                        setEscolhendoResponsavel(false);
                        setConfirmandoSaida(true);
                      }}
                      style={{
                        background: corResponsavel + '18', border: `1px solid ${corResponsavel}55`,
                        borderRadius: '9px', padding: '0.75rem 1rem',
                        color: corResponsavel, fontFamily: "'Bebas Neue', cursive",
                        fontSize: '1.1rem', letterSpacing: '0.08em',
                        cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = corResponsavel + '30'; e.currentTarget.style.borderColor = corResponsavel; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = corResponsavel + '18'; e.currentTarget.style.borderColor = corResponsavel + '55'; }}
                    >
                      {nome}
                    </button>;
                  })}
                  <button
                    onClick={() => { setEscolhendoResponsavel(false); setEscolhendoSetorAtendimento(true); }}
                    style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', padding: '0.3rem' }}
                  >
                    Voltar
                  </button>
                </div>
              ) : escolhendoSetorAcabamento ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: '0 0 4px 0' }}>
                    Escolha o próximo setor:
                  </p>
                  {['Corte', 'Expedição'].map((setor) => {
                    const cor = COR_SETOR[setor];
                    return (
                      <button
                        key={setor}
                        onClick={() => { setSetorAcabamentoSelecionado(setor); setEscolhendoSetorAcabamento(false); setConfirmandoSaida(true); }}
                        style={{
                          background: cor + '18', border: `1px solid ${cor}55`,
                          borderRadius: '9px', padding: '0.75rem 1rem',
                          color: cor, fontFamily: "'Bebas Neue', cursive",
                          fontSize: '1.1rem', letterSpacing: '0.08em',
                          cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = cor + '30'; e.currentTarget.style.borderColor = cor; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = cor + '18'; e.currentTarget.style.borderColor = cor + '55'; }}
                      >
                        {setor}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setEscolhendoSetorAcabamento(false)}
                    style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', padding: '0.3rem' }}
                  >
                    Cancelar
                  </button>
                </div>
              ) : escolhendoSetorAtendimento ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: '0 0 4px 0' }}>
                    Escolha o próximo setor:
                  </p>
                  {['Arte Finalista', 'Corte', 'Expedição'].map((setor) => {
                    const cor = COR_SETOR[setor];
                    return (
                      <button
                        key={setor}
                        onClick={() => {
                          setSetorAtendimentoSelecionado(setor);
                          setEscolhendoSetorAtendimento(false);
                          if (setor === 'Arte Finalista') {
                            setEscolhendoResponsavel(true);
                          } else {
                            setResponsavelSelecionado(null);
                            setConfirmandoSaida(true);
                          }
                        }}
                        style={{
                          background: cor + '18', border: `1px solid ${cor}55`,
                          borderRadius: '9px', padding: '0.75rem 1rem',
                          color: cor, fontFamily: "'Bebas Neue', cursive",
                          fontSize: '1.1rem', letterSpacing: '0.08em',
                          cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = cor + '30'; e.currentTarget.style.borderColor = cor; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = cor + '18'; e.currentTarget.style.borderColor = cor + '55'; }}
                      >
                        {setor}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setEscolhendoSetorAtendimento(false)}
                    style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', padding: '0.3rem' }}
                  >
                    Cancelar
                  </button>
                </div>
              ) : escolhendoProximoSetorFora ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: '0 0 4px 0' }}>
                    Escolha o próximo setor:
                  </p>
                  {['Acabamento', 'Corte', 'Expedição'].map((setor) => {
                    const cor = COR_SETOR[setor];
                    return (
                      <button
                        key={setor}
                        onClick={() => { setSetorForaSelecionado(setor); setEscolhendoProximoSetorFora(false); setConfirmandoSaida(true); }}
                        style={{
                          background: cor + '18', border: `1px solid ${cor}55`,
                          borderRadius: '9px', padding: '0.75rem 1rem',
                          color: cor, fontFamily: "'Bebas Neue', cursive",
                          fontSize: '1.1rem', letterSpacing: '0.08em',
                          cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = cor + '30'; e.currentTarget.style.borderColor = cor; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = cor + '18'; e.currentTarget.style.borderColor = cor + '55'; }}
                      >
                        {setor}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setEscolhendoProximoSetorFora(false)}
                    style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', padding: '0.3rem' }}
                  >
                    Cancelar
                  </button>
                </div>
              ) : escolhendoSetorCorte ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: '0 0 4px 0' }}>
                    Escolha o próximo setor:
                  </p>
                  {['Acabamento', 'Expedição'].map((setor) => {
                    const cor = COR_SETOR[setor];
                    return (
                      <button
                        key={setor}
                        onClick={() => { setSetorCorteSelecionado(setor); setEscolhendoSetorCorte(false); setConfirmandoSaida(true); }}
                        style={{
                          background: cor + '18', border: `1px solid ${cor}55`,
                          borderRadius: '9px', padding: '0.75rem 1rem',
                          color: cor, fontFamily: "'Bebas Neue', cursive",
                          fontSize: '1.1rem', letterSpacing: '0.08em',
                          cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = cor + '30'; e.currentTarget.style.borderColor = cor; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = cor + '18'; e.currentTarget.style.borderColor = cor + '55'; }}
                      >
                        {setor}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setEscolhendoSetorCorte(false)}
                    style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', padding: '0.3rem' }}
                  >
                    Cancelar
                  </button>
                </div>
              ) : escolhendoSetorImpressaoDigital ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: '0 0 4px 0' }}>
                    Escolha o próximo setor:
                  </p>
                  {['Acabamento', 'Corte', 'Expedição'].map((setor) => {
                    const cor = COR_SETOR[setor];
                    return (
                      <button
                        key={setor}
                        onClick={() => { setSetorImpressaoDigitalSelecionado(setor); setEscolhendoSetorImpressaoDigital(false); setConfirmandoSaida(true); }}
                        style={{
                          background: cor + '18', border: `1px solid ${cor}55`,
                          borderRadius: '9px', padding: '0.75rem 1rem',
                          color: cor, fontFamily: "'Bebas Neue', cursive",
                          fontSize: '1.1rem', letterSpacing: '0.08em',
                          cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = cor + '30'; e.currentTarget.style.borderColor = cor; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = cor + '18'; e.currentTarget.style.borderColor = cor + '55'; }}
                      >
                        {setor}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setEscolhendoSetorImpressaoDigital(false)}
                    style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', padding: '0.3rem' }}
                  >
                    Cancelar
                  </button>
                </div>
              ) : confirmandoSaida ? (
                <div style={{
                  background: 'rgba(240,165,0,0.06)', border: '1px solid rgba(240,165,0,0.2)',
                  borderRadius: '10px', padding: '1rem',
                }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', color: '#e0e0e0', fontWeight: 600, margin: '0 0 0.35rem 0' }}>
                    Tem certeza?
                  </p>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: '0 0 1rem 0', lineHeight: 1.4 }}>
                    {`O item será enviado para ${destinoDireto || (tipoSelecionado === 'Impressão Offset' ? 'Pré Produção' : tipoSelecionado) || setorAtendimentoSelecionado || setorAcabamentoSelecionado || setorForaSelecionado || setorCorteSelecionado || setorImpressaoDigitalSelecionado || PROXIMO_SETOR_FIXO[item.setor_atual] || 'o próximo setor'}.`}
                    {setorAtendimentoSelecionado === 'Arte Finalista' && responsavelSelecionado && ` Responsável: ${responsavelSelecionado}.`}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setConfirmandoSaida(false);
                        if (tipoSelecionado || destinoDireto) setEscolhendoTipo(true);
                        else if (setorAtendimentoSelecionado === 'Arte Finalista') setEscolhendoResponsavel(true);
                        else if (setorAtendimentoSelecionado) setEscolhendoSetorAtendimento(true);
                        else if (setorAcabamentoSelecionado) setEscolhendoSetorAcabamento(true);
                        else if (setorForaSelecionado) setEscolhendoProximoSetorFora(true);
                        else if (setorCorteSelecionado) setEscolhendoSetorCorte(true);
                        else if (setorImpressaoDigitalSelecionado) setEscolhendoSetorImpressaoDigital(true);
                      }}
                      style={{
                        background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '8px',
                        padding: '0.75rem 0', color: '#888',
                        fontFamily: "'Bebas Neue', cursive", fontSize: '1.15rem', letterSpacing: '0.08em',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#aaa'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
                    >
                      NÃO
                    </button>
                    <button
                      onClick={biparSaida}
                      disabled={bipando}
                      style={{
                        background: '#f0a500', border: '1px solid #f0a500', borderRadius: '8px',
                        padding: '0.75rem 0', color: '#0d0d0d',
                        fontFamily: "'Bebas Neue', cursive", fontSize: '1.15rem', letterSpacing: '0.08em',
                        cursor: bipando ? 'not-allowed' : 'pointer', opacity: bipando ? 0.6 : 1,
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { if (!bipando) e.currentTarget.style.background = '#d9940a'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#f0a500'; }}
                    >
                      {bipando ? 'AGUARDE...' : 'SIM'}
                    </button>
                  </div>
                </div>
              ) : !todosConcluidos ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  background: 'rgba(240,165,0,0.06)', border: '1px solid rgba(240,165,0,0.2)',
                  borderRadius: '10px', padding: '0.85rem 1rem',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f0a500" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#f0a500' }}>
                    Conclua todos os processos antes de dar saída. ({concluidos}/{processos.length})
                  </span>
                </div>
              ) : item.setor_atual === 'Expedição' ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.2)',
                  borderRadius: '10px', padding: '0.85rem 1rem',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#4ade80' }}>
                    Checklist completo. O despacho da Expedição é feito pela O.S inteira, no card agrupado da coluna — feche esse item e use o botão "Despachar" lá.
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => {
                    if (item.setor_atual === 'Arte Finalista') setEscolhendoTipo(true);
                    else if (item.setor_atual === 'Impressão Fora') setEscolhendoProximoSetorFora(true);
                    else if (item.setor_atual === 'Atendimento') setEscolhendoSetorAtendimento(true);
                    else if (item.setor_atual === 'Acabamento') setEscolhendoSetorAcabamento(true);
                    else if (item.setor_atual === 'Corte') setEscolhendoSetorCorte(true);
                    else if (item.setor_atual === 'Impressão Digital') setEscolhendoSetorImpressaoDigital(true);
                    else setConfirmandoSaida(true);
                  }}
                  style={{
                    width: '100%', background: '#f0a500', border: '1px solid #f0a500',
                    borderRadius: '10px', padding: '0.9rem 0', color: '#0d0d0d',
                    fontFamily: "'Bebas Neue', cursive", fontSize: '1.3rem', letterSpacing: '0.08em',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#d9940a'; e.currentTarget.style.borderColor = '#d9940a'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f0a500'; e.currentTarget.style.borderColor = '#f0a500'; }}
                >
                  SAÍDA
                </button>
              )}
            </div>
          )}

          {/* ── Seção de retrocesso ─────────────────────────────────────────── */}
          {(setorAnterior || item.setor_atual !== 'Atendimento') && item.setor_atual !== 'Atendimento' && (
            <div style={{
              marginTop: '1.5rem',
              background: 'rgba(224,51,51,0.06)',
              border: '1px solid rgba(224,51,51,0.18)',
              borderRadius: '12px',
              padding: '1.25rem',
            }}>
              {!confirmando ? (
                <>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: '#e05555', fontWeight: 700, margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.8 }}>
                    Zona de retrocesso
                  </p>
                  <button
                    onClick={() => { setConfirmando(true); setMotivoRetro(''); }}
                    style={{
                      width: '100%',
                      background: 'rgba(224,51,51,0.14)',
                      border: '1.5px solid rgba(224,51,51,0.5)',
                      borderRadius: '10px',
                      padding: '0.9rem',
                      color: '#e05555',
                      fontFamily: "'Bebas Neue', cursive",
                      fontSize: '1.25rem',
                      letterSpacing: '0.08em',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(224,51,51,0.24)'; e.currentTarget.style.borderColor = '#e05555'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(224,51,51,0.14)'; e.currentTarget.style.borderColor = 'rgba(224,51,51,0.5)'; }}
                  >
                    ↩ {setorAnterior ? `RETROCEDER PARA ${setorAnterior.toUpperCase()}` : 'RETROCEDER'}
                  </button>
                </>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                    <span style={{ background: cor + '20', color: cor, borderRadius: '20px', padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700 }}>{item.setor_atual}</span>
                    <span style={{ color: '#e05555', fontSize: '1rem' }}>↩</span>
                    {setorAnterior
                      ? <span style={{ background: corAnterior + '20', color: corAnterior, borderRadius: '20px', padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700 }}>{setorAnterior}</span>
                      : <span style={{ background: '#33333320', color: '#888', borderRadius: '20px', padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700 }}>setor anterior</span>
                    }
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: '#e05555', fontWeight: 600, margin: '0 0 6px 0' }}>
                      Motivo <span style={{ opacity: 0.6, fontWeight: 400 }}>(obrigatório)</span>
                    </p>
                    <textarea
                      autoFocus
                      value={motivoRetro}
                      onChange={(e) => setMotivoRetro(e.target.value)}
                      placeholder="Descreva o motivo..."
                      rows={3}
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        background: 'rgba(224,51,51,0.06)', border: '1px solid rgba(224,51,51,0.3)',
                        borderRadius: '8px', padding: '8px 10px', outline: 'none', resize: 'none',
                        color: '#e8e8e8', fontSize: '0.88rem', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.4,
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#e05555'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(224,51,51,0.3)'}
                    />
                  </div>

                  {erroRetro && <p style={{ color: '#e05555', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erroRetro}</p>}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <button
                      onClick={() => setConfirmando(false)}
                      style={{ background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '9px', padding: '0.8rem', color: '#888', fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em', cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#aaa'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
                    >
                      CANCELAR
                    </button>
                    <button
                      onClick={confirmarRetrograde}
                      disabled={carregandoRetro || !motivoRetro.trim()}
                      style={{
                        background: motivoRetro.trim() ? 'rgba(224,51,51,0.2)' : 'rgba(224,51,51,0.05)',
                        border: '1.5px solid #e05555', borderRadius: '9px', padding: '0.8rem', color: '#e05555',
                        fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em',
                        cursor: (carregandoRetro || !motivoRetro.trim()) ? 'not-allowed' : 'pointer',
                        opacity: !motivoRetro.trim() ? 0.45 : 1,
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { if (!carregandoRetro && motivoRetro.trim()) e.currentTarget.style.background = 'rgba(224,51,51,0.32)'; }}
                      onMouseLeave={(e) => { if (!carregandoRetro && motivoRetro.trim()) e.currentTarget.style.background = 'rgba(224,51,51,0.2)'; }}
                    >
                      {carregandoRetro ? 'AGUARDE...' : 'CONFIRMAR'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL DE BUSCA DE O.S
// Permite buscar uma Ordem de Serviço pelo número e ver todos os seus itens
// com seus setores atuais destacados pelas cores do Kanban.
//
// Props:
//   • onClose → fecha o modal
// ─────────────────────────────────────────────────────────────────────────────
function ModalBuscarOS({ onClose, onDeletado }) {
  const [numero, setNumero]             = useState('');
  const [resultado, setResultado]       = useState(null);
  const [carregando, setCarregando]     = useState(false);
  const [erro, setErro]                 = useState('');
  const [confirmandoDeletar, setConfirmandoDeletar] = useState(false);
  const [deletando, setDeletando]       = useState(false);
  // Guarda o número do item que está aguardando confirmação de delete (null = nenhum)
  const [itemConfirmando, setItemConfirmando] = useState(null);
  const inputRef = useRef(null);

  // Foca o input ao abrir o modal
  useEffect(() => { inputRef.current?.focus(); }, []);

  async function buscar(e) {
    e.preventDefault();
    if (!numero.trim()) return;
    setCarregando(true);
    setErro('');
    setResultado(null);
    setConfirmandoDeletar(false);
    try {
      const data = await api.getOrdem(numero.trim(), true);
      setResultado(data);
    } catch (err) {
      setErro(err.message);
    } finally {
      setCarregando(false);
    }
  }

  async function confirmarDeletar() {
    setDeletando(true);
    try {
      await api.deletarOrdem(resultado.numero);
      onDeletado();
      onClose();
    } catch (err) {
      setErro(err.message);
      setDeletando(false);
      setConfirmandoDeletar(false);
    }
  }

  // Apaga um item específico e recarrega os dados da O.S no modal
  async function deletarItemDaOS(numeroItem) {
    try {
      await api.deletarItem(numeroItem);
      // Recarrega a O.S para refletir o item removido
      const atualizado = await api.getOrdem(resultado.numero, true);
      setResultado(atualizado);
      setItemConfirmando(null);
    } catch (err) {
      setErro(err.message);
      setItemConfirmando(null);
    }
  }

  // Estilos compartilhados para os inputs deste modal
  const inputStyle = {
    flex: 1, background: '#181818', border: '1px solid #2a2a2a',
    borderRadius: '10px', padding: '0.75rem 1rem', color: '#e8e8e8',
    fontSize: '1rem', fontFamily: "'JetBrains Mono', monospace", outline: 'none',
    transition: 'border-color 0.18s',
  };

  return (
    // Overlay: clique fora fecha o modal
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', backdropFilter: 'blur(4px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#141414', border: '1px solid #2a2a2a', borderTop: '4px solid #f0a500', borderRadius: '16px', width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto', fontFamily: "'DM Sans', sans-serif" }}
      >
        <div style={{ padding: '1.75rem' }}>

          {/* Cabeçalho */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h2 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.5rem', color: '#e0e0e0', letterSpacing: '0.08em', margin: 0 }}>
              BUSCAR ORDEM DE SERVIÇO
            </h2>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#555', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 1rem', flexShrink: 0 }}>×</button>
          </div>

          {/* Formulário de busca */}
          <form onSubmit={buscar} style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem' }}>
            <input
              ref={inputRef}
              value={numero}
              onChange={(e) => { setNumero(e.target.value); setErro(''); setResultado(null); }}
              placeholder="Número da O.S..."
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#f0a500'}
              onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
            />
            <button
              type="submit"
              disabled={carregando}
              style={{ background: '#f0a500', border: 'none', borderRadius: '10px', padding: '0.75rem 1.25rem', color: '#0d0d0d', fontFamily: "'Bebas Neue', cursive", fontSize: '1.15rem', letterSpacing: '0.08em', cursor: carregando ? 'not-allowed' : 'pointer', flexShrink: 0 }}
            >
              {carregando ? '...' : 'BUSCAR'}
            </button>
          </form>

          {/* Mensagem de erro */}
          {erro && (
            <div style={{ background: 'rgba(224,51,51,0.07)', border: '1px solid rgba(224,51,51,0.2)', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1rem' }}>
              <p style={{ color: '#e05555', fontSize: '0.9rem', margin: 0 }}>{erro}</p>
            </div>
          )}

          {/* Resultado da busca */}
          {resultado && (
            <div>
              {/* Cabeçalho da O.S */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1a1a1a', border: '1px solid #252525', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '12px' }}>
                <div>
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem', color: '#f0a500', fontWeight: 700, margin: 0, marginBottom: '3px' }}>
                    O.S {resultado.numero}
                  </p>
                  <p style={{ fontSize: '1rem', color: '#e0e0e0', fontWeight: 600, margin: 0 }}>
                    {resultado.cliente}
                  </p>
                </div>
                {/* Badge com contagem de itens */}
                <span style={{ background: 'rgba(240,165,0,0.12)', color: '#f0a500', borderRadius: '20px', padding: '3px 14px', fontSize: '0.82rem', fontWeight: 700, flexShrink: 0 }}>
                  {resultado.itens.length} {resultado.itens.length === 1 ? 'item' : 'itens'}
                </span>
              </div>

              {/* Aviso: O.S inteira já concluída — todos os itens estão em "Concluído" */}
              {resultado.itens.length > 0 && resultado.itens.every(i => i.setor_atual === 'Concluído') && (
                <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '12px' }}>
                  <p style={{ color: '#4ade80', fontSize: '0.9rem', margin: 0, fontWeight: 600 }}>
                    ✓ Esta O.S já foi concluída — está na aba "Concluídos".
                  </p>
                </div>
              )}

              {/* Lista de itens com setor atual e botão de delete por item */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {resultado.itens.map((item) => {
                  const cor = COR_SETOR[item.setor_atual] || '#888';
                  const confirmando = itemConfirmando === item.numero;
                  return (
                    <div key={item.id} style={{ background: '#1a1a1a', border: `1px solid ${confirmando ? 'rgba(224,51,51,0.35)' : '#252525'}`, borderLeft: `4px solid ${cor}`, borderRadius: '9px', overflow: 'hidden', transition: 'border-color 0.15s' }}>

                      {/* Linha principal do item */}
                      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Número + setor na mesma linha */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8rem', color: '#f0a500', fontWeight: 700 }}>
                              {item.numero}
                            </span>
                            <span style={{ background: cor + '20', color: cor, borderRadius: '20px', padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}>
                              {item.setor_atual}
                            </span>
                          </div>
                          {/* Descrição */}
                          <p style={{ fontSize: '0.88rem', color: '#d8d8d8', margin: 0, marginBottom: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.descricao}
                          </p>
                          {/* Status de entrada */}
                          {item.setor_atual === 'Concluído' ? (
                            <span style={{
                              display: 'inline-block',
                              background: 'rgba(74,222,128,0.12)',
                              border: '1px solid rgba(74,222,128,0.35)',
                              borderRadius: '999px',
                              padding: '2px 10px',
                              fontSize: '0.7rem',
                              color: '#4ade80',
                              fontFamily: "'Bebas Neue', cursive",
                              letterSpacing: '0.08em',
                            }}>✓ Concluído</span>
                          ) : item.entrada_em ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                              </svg>
                              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: '#4ade80' }}>
                                Em andamento · {formatarTempo(item.entrada_em)}
                              </span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#f0a500" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                              </svg>
                              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: '#f0a500' }}>
                                Entrada não registrada
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Botão de lixeira — abre confirmação inline */}
                        <button
                          onClick={() => setItemConfirmando(confirmando ? null : item.numero)}
                          title="Deletar item"
                          style={{ background: 'transparent', border: 'none', color: confirmando ? '#e05555' : '#444', cursor: 'pointer', padding: '0 0 0 4px', flexShrink: 0, transition: 'color 0.15s', lineHeight: 1 }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#e05555'}
                          onMouseLeave={(e) => e.currentTarget.style.color = confirmando ? '#e05555' : '#444'}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </button>
                      </div>

                      {/* Confirmação inline de delete do item */}
                      {confirmando && (
                        <div style={{ borderTop: '1px solid rgba(224,51,51,0.2)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', background: 'rgba(224,51,51,0.05)' }}>
                          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: 0 }}>
                            Deletar este item e seu histórico?
                          </p>
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <button
                              onClick={() => setItemConfirmando(null)}
                              style={{ background: '#252525', border: '1px solid #333', borderRadius: '7px', padding: '5px 12px', color: '#888', fontFamily: "'Bebas Neue', cursive", fontSize: '0.95rem', letterSpacing: '0.06em', cursor: 'pointer' }}
                            >
                              NÃO
                            </button>
                            <button
                              onClick={() => deletarItemDaOS(item.numero)}
                              style={{ background: 'rgba(224,51,51,0.2)', border: '1px solid #e05555', borderRadius: '7px', padding: '5px 12px', color: '#e05555', fontFamily: "'Bebas Neue', cursive", fontSize: '0.95rem', letterSpacing: '0.06em', cursor: 'pointer' }}
                            >
                              SIM
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Botão de deletar / confirmação */}
              {!confirmandoDeletar ? (
                <button
                  onClick={() => setConfirmandoDeletar(true)}
                  style={{
                    width: '100%', marginTop: '14px',
                    background: 'rgba(224,51,51,0.07)', border: '1px solid rgba(224,51,51,0.25)',
                    borderRadius: '10px', padding: '0.8rem', color: '#e05555',
                    fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(224,51,51,0.14)'; e.currentTarget.style.borderColor = '#e05555'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(224,51,51,0.07)'; e.currentTarget.style.borderColor = 'rgba(224,51,51,0.25)'; }}
                >
                  DELETAR ESTA O.S
                </button>
              ) : (
                <div style={{ marginTop: '14px', background: 'rgba(224,51,51,0.07)', border: '1px solid rgba(224,51,51,0.25)', borderRadius: '10px', padding: '1rem' }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', color: '#e0e0e0', fontWeight: 600, margin: '0 0 0.25rem 0' }}>
                    Tem certeza?
                  </p>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#888', margin: '0 0 1rem 0', lineHeight: 1.4 }}>
                    Esta ação apaga a O.S, todos os seus itens e todo o histórico de bipagens. Não pode ser desfeita.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <button
                      onClick={() => setConfirmandoDeletar(false)}
                      style={{ background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '0.75rem', color: '#888', fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em', cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#aaa'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
                    >
                      NÃO
                    </button>
                    <button
                      onClick={confirmarDeletar}
                      disabled={deletando}
                      style={{ background: 'rgba(224,51,51,0.15)', border: '1px solid #e05555', borderRadius: '8px', padding: '0.75rem', color: '#e05555', fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em', cursor: deletando ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={(e) => { if (!deletando) e.currentTarget.style.background = 'rgba(224,51,51,0.28)'; }}
                      onMouseLeave={(e) => { if (!deletando) e.currentTarget.style.background = 'rgba(224,51,51,0.15)'; }}
                    >
                      {deletando ? 'DELETANDO...' : 'SIM, DELETAR'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL DE NOVA O.S
// Formulário para criar uma nova Ordem de Serviço com um ou mais itens.
// Os itens podem ser adicionados dinamicamente com o botão "+ Adicionar item".
//
// Props:
//   • onClose  → fecha o modal
//   • onCriado → chamado após criar (atualiza o Kanban)
// ─────────────────────────────────────────────────────────────────────────────
function ModalNovaOS({ onClose, onCriado, onIrParaReconciliar }) {
  const [numero, setNumero]               = useState('');
  const [numeroPedido, setNumeroPedido]         = useState('');
  const [cliente, setCliente]                   = useState('');
  const [proximoSufixo, setProximoSufixo]       = useState(1);
  const [clienteComHistorico, setClienteComHistorico] = useState(false);
  const [itens, setItens]                       = useState([{ descricao: '', numero: null }]);
  const [carregando, setCarregando]             = useState(false);
  const [buscando, setBuscando]                 = useState(false);
  const [erro, setErro]                         = useState('');
  const [osExistente, setOsExistente]           = useState(null);
  const [buscandoOS, setBuscandoOS]             = useState(false);
  const [aguardando, setAguardando]             = useState(false);
  const [buscaFeita, setBuscaFeita]             = useState(false);
  const [veioDoPDV, setVeioDoPDV]               = useState(false);
  const [email, setEmail]                       = useState('');
  const [telefone, setTelefone]                 = useState('');

  // Monta o número completo do item baseado no índice
  function numeroItem(i) {
    if (itens[i]?.numero) return itens[i].numero;
    if (!numeroPedido.trim()) return null;
    return `${numeroPedido.trim()}-${String(proximoSufixo + i).padStart(3, '0')}`;
  }

  function addItem() {
    setItens((prev) => [...prev, { descricao: '', numero: null }]);
  }

  function removeItem(i) {
    setItens((prev) => prev.filter((_, idx) => idx !== i));
  }

  function updateDescricao(i, value) {
    setItens((prev) => prev.map((item, idx) => idx === i ? { ...item, descricao: value } : item));
  }

  // Busca histórico pelo cliente ou pelo número do pedido e preenche os campos automaticamente
  async function buscarHistorico({ porCliente, porNumeroPedido }) {
    setBuscando(true);
    setErro('');
    try {
      const params = porCliente
        ? { cliente: porCliente }
        : { numeroPedido: porNumeroPedido };
      const data = await api.buscarHistorico(params);
      setNumeroPedido(data.numero_pedido);
      setCliente(data.cliente);
      setProximoSufixo(data.proximo_sufixo);
      setClienteComHistorico(true); // trava o campo Nº Pedido PDV
    } catch {
      // Sem histórico = cliente novo, começa do sufixo 1
      setProximoSufixo(1);
      setClienteComHistorico(false);
    } finally {
      setBuscando(false);
    }
  }

  async function verificarOS(valorAtual) {
    const num = (valorAtual !== undefined ? valorAtual : numero).trim();
    if (!num) return;
    setAguardando(false);
    setBuscandoOS(true);
    setOsExistente(null);
    setCliente('');
    setNumeroPedido('');
    setProximoSufixo(1);
    setClienteComHistorico(false);
    setVeioDoPDV(false);
    setItens([{ descricao: '', numero: null }]);
    setEmail('');
    setTelefone('');
    setErro('');
    try {
      const data = await api.getOrdem(num);
      setOsExistente(data);
      setCliente(data.cliente);
      buscarHistorico({ porCliente: data.cliente });
    } catch {
      // O.S não existe no CRM — tenta buscar no PDV
      try {
        const pdv = await api.buscarPedidoPDV(num);
        setCliente(pdv.cliente);
        setNumeroPedido(pdv.numeroPedido);
        setProximoSufixo(pdv.sufixoInicial);
        setClienteComHistorico(true);
        setItens(pdv.itens.map(item => ({ descricao: item.descricao, numero: item.numero, qtde: item.qtde })));
        setEmail(pdv.email || '');
        setTelefone(pdv.telefone || '');
        setVeioDoPDV(true);
      } catch {
        setVeioDoPDV(false);
      }
    } finally {
      setBuscandoOS(false);
      setBuscaFeita(true);
    }
  }

  async function criar(e) {
    e.preventDefault();
    setErro('');

    if (!numero.trim()) {
      return setErro('Informe o número da O.S.');
    }
    if (osExistente) {
      return setErro(`A O.S ${osExistente.numero} já existe no Kanban e não pode ser adicionada novamente.`);
    }
    if (!veioDoPDV) {
      return setErro('Esta O.S não foi encontrada no PDV. Só é possível cadastrar pedidos que existam no PDV.');
    }
    if (!cliente.trim()) {
      return setErro('Cliente é obrigatório.');
    }
    if (!numeroPedido.trim()) {
      return setErro('Número do pedido PDV é obrigatório.');
    }
    if (itens.some((item) => !item.descricao.trim())) {
      return setErro('Todos os itens precisam de descrição.');
    }

    setCarregando(true);
    try {
      const resultado = await api.criarOrdem(
        numero.trim(),
        cliente.trim(),
        itens.map((item, i) => ({
          numero: numeroItem(i),
          descricao: item.descricao.trim(),
          qtde: typeof item.qtde === 'number' ? item.qtde : null,
        })),
        email || null,
        telefone || null
      );
      onCriado();
      onClose();
      if (resultado?.itensRenomeados?.length > 0) {
        const lista = resultado.itensRenomeados.map((r) => `${r.numeroOriginal} → ${r.numeroFinal}`).join('\n');
        alert(`O PDV reaproveitou o número de ${resultado.itensRenomeados.length === 1 ? 'um item' : 'alguns itens'}. Número ajustado automaticamente:\n\n${lista}`);
      }
    } catch (err) {
      setErro(err.message);
    } finally {
      setCarregando(false);
    }
  }

  const inputStyle = {
    background: '#181818', border: '1px solid #2a2a2a', borderRadius: '9px',
    padding: '0.72rem 1rem', color: '#e8e8e8', fontSize: '0.95rem',
    fontFamily: "'DM Sans', sans-serif", outline: 'none',
    width: '100%', boxSizing: 'border-box', transition: 'border-color 0.18s',
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', backdropFilter: 'blur(4px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#141414', border: '1px solid #2a2a2a', borderTop: '4px solid #f0a500', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', fontFamily: "'DM Sans', sans-serif" }}
      >
        <div style={{ padding: '1.75rem' }}>

          {/* Cabeçalho */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h2 style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.5rem', color: '#e0e0e0', letterSpacing: '0.08em', margin: 0 }}>
              NOVA ORDEM DE SERVIÇO
            </h2>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#555', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 1rem', flexShrink: 0 }}>×</button>
          </div>

          <form onSubmit={criar}>

            {/* Nº OP */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#555', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                Nº OP
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  value={numero}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNumero(val);
                    setOsExistente(null);
                    setCliente('');
                    setVeioDoPDV(false);
                    setAguardando(false);
                    setBuscaFeita(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      verificarOS();
                    }
                  }}
                  placeholder="3579"
                  style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace", flex: 1 }}
                  onFocus={(e) => e.target.style.borderColor = '#f0a500'}
                  onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
                />
                <button
                  type="button"
                  onClick={() => verificarOS()}
                  disabled={!numero.trim() || buscandoOS}
                  style={{
                    background: '#f0a500', border: 'none', borderRadius: '9px',
                    padding: '0 1rem', color: '#0d0d0d', fontFamily: "'Bebas Neue', cursive",
                    fontSize: '1rem', letterSpacing: '0.06em', cursor: numero.trim() && !buscandoOS ? 'pointer' : 'not-allowed',
                    opacity: !numero.trim() || buscandoOS ? 0.4 : 1, flexShrink: 0, transition: 'opacity 0.15s',
                  }}
                >
                  Buscar
                </button>
              </div>
            </div>

            {(aguardando || buscandoOS) && (
              <p style={{ fontSize: '0.8rem', color: '#555', margin: '0 0 10px 2px' }}>Verificando O.S...</p>
            )}

            {/* OS já existe no Kanban — bloqueia tudo, só mostra erro e botão fechar */}
            {!aguardando && osExistente && (
              <>
                <div style={{ background: 'rgba(224,51,51,0.07)', border: '1px solid rgba(224,51,51,0.2)', borderRadius: '9px', padding: '14px 16px', marginBottom: '1.25rem' }}>
                  <p style={{ fontSize: '0.88rem', color: '#e05555', fontWeight: 700, margin: '0 0 6px 0' }}>
                    O.S {osExistente.numero} {osExistente.origem_webhook && osExistente.itens.some((i) => !i.data_entrega) ? 'possui itens aguardando data de entrega' : 'já está em produção'}
                  </p>
                  <p style={{ fontSize: '0.82rem', color: '#888', margin: 0, lineHeight: 1.5 }}>
                    Esta O.S pertence a <strong style={{ color: '#aaa' }}>{osExistente.cliente}</strong> e possui {osExistente.itens.length} {osExistente.itens.length === 1 ? 'item cadastrado' : 'itens cadastrados'}. {osExistente.origem_webhook && osExistente.itens.some((i) => !i.data_entrega) ? 'Informe a data de cada item no pop-up ou em “Definir datas”.' : 'Não é possível adicionar pedidos já existentes.'}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{ flex: 1, background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '0.85rem', color: '#888', fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#aaa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
                  >
                    FECHAR
                  </button>
                  <button
                    type="button"
                    onClick={() => onIrParaReconciliar(osExistente.numero)}
                    style={{ flex: 1, background: '#00c9db', border: '1px solid #00c9db', borderRadius: '10px', padding: '0.85rem', color: '#0d0d0d', fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em', cursor: 'pointer', fontWeight: 700 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#00aebe'; e.currentTarget.style.borderColor = '#00aebe'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#00c9db'; e.currentTarget.style.borderColor = '#00c9db'; }}
                  >
                    RECONCILIAR
                  </button>
                </div>
              </>
            )}

            {/* Formulário normal — só aparece quando OS veio do PDV */}
            {!osExistente && (<>
              {buscaFeita && !buscandoOS && !veioDoPDV && (
                <div style={{ background: 'rgba(224,51,51,0.07)', border: '1px solid rgba(224,51,51,0.2)', borderRadius: '9px', padding: '10px 14px', marginBottom: '10px' }}>
                  <p style={{ fontSize: '0.8rem', color: '#e05555', fontWeight: 700, margin: 0 }}>Pedido não encontrado no PDV</p>
                  <p style={{ fontSize: '0.78rem', color: '#888', margin: '2px 0 0 0' }}>Só é possível cadastrar pedidos que existam no PDV</p>
                </div>
              )}
              {!aguardando && veioDoPDV && (
                <div style={{ background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.25)', borderRadius: '9px', padding: '10px 14px', marginBottom: '10px' }}>
                  <p style={{ fontSize: '0.8rem', color: '#06b6d4', fontWeight: 700, margin: 0 }}>
                    Dados importados do PDV — {itens.length} {itens.length === 1 ? 'item encontrado' : 'itens encontrados'}
                  </p>
                  <p style={{ fontSize: '0.78rem', color: '#888', margin: '2px 0 0 0' }}>Verifique as descrições antes de confirmar</p>
                </div>
              )}

              {veioDoPDV && (<>
                {/* Cliente */}
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#555', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Cliente</label>
                  <input value={cliente} disabled style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} />
                  <p style={{ fontSize: '0.75rem', color: '#555', margin: '5px 0 0 2px' }}>Preenchido automaticamente — não pode ser alterado</p>
                </div>

                {/* Nº Pedido PDV */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#555', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Nº Pedido PDV</label>
                  <input value={numeroPedido} disabled style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace", opacity: 0.6, cursor: 'not-allowed', borderColor: '#f0a50040' }} />
                </div>

                {/* Itens */}
                <div style={{ borderTop: '1px solid #1e1e1e', paddingTop: '1.25rem', marginBottom: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', color: '#c8c8c8', letterSpacing: '0.08em' }}>ITENS A ADICIONAR</span>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem', color: '#555' }}>{itens.length} {itens.length === 1 ? 'item' : 'itens'}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                    {itens.map((item, i) => {
                      const num = numeroItem(i);
                      return (
                        <div key={i} style={{ background: '#1a1a1a', border: '1px solid #252525', borderRadius: '10px', padding: '12px' }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.72rem', color: num ? '#f0a500' : '#444', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            {num || `ITEM ${i + 1}`}
                          </span>
                          <input
                            value={item.descricao}
                            onChange={(e) => updateDescricao(i, e.target.value)}
                            placeholder="Descrição do item"
                            style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#f0a500'}
                            onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Erro e ações */}
                {erro && (
                  <div style={{ background: 'rgba(224,51,51,0.07)', border: '1px solid rgba(224,51,51,0.2)', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1rem' }}>
                    <p style={{ color: '#e05555', fontSize: '0.88rem', margin: 0 }}>{erro}</p>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px' }}>
                  <button type="button" onClick={onClose} style={{ background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '0.85rem', color: '#888', fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#aaa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}>
                    CANCELAR
                  </button>
                  <button type="submit" disabled={carregando || buscandoOS}
                    style={{ background: '#f0a500', border: 'none', borderRadius: '10px', padding: '0.85rem', color: '#0d0d0d', fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em', cursor: (carregando || buscandoOS) ? 'not-allowed' : 'pointer', opacity: (carregando || buscandoOS) ? 0.7 : 1 }}>
                    {carregando ? 'SALVANDO...' : 'CONFIRMAR'}
                  </button>
                </div>
              </>)}

              {/* Se buscou e não veio do PDV, só botão fechar */}
              {buscaFeita && !veioDoPDV && (
                <button type="button" onClick={onClose} style={{ width: '100%', marginTop: '8px', background: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '0.85rem', color: '#888', fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', letterSpacing: '0.08em', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#aaa'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}>
                  FECHAR
                </button>
              )}
            </>)}
          </form>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE: ModalPerfil
// Modal simples para o funcionário editar o próprio nome de exibição.
// ─────────────────────────────────────────────────────────────────────────────
function ModalPerfil({ usuario, onClose, onSalvar }) {
  const [nome, setNome]         = useState(usuario.nome || '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro]         = useState('');
  const [ok, setOk]             = useState(false);

  async function salvar() {
    if (nome.trim().length < 2) { setErro('Nome deve ter pelo menos 2 caracteres.'); return; }
    setSalvando(true);
    setErro('');
    try {
      const atualizado = await api.atualizarNome(nome.trim());
      setOk(true);
      onSalvar(atualizado);
      setTimeout(onClose, 900);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#141414', border: '1px solid #2a2a2a',
          borderTop: '4px solid #f0a500', borderRadius: '16px',
          width: '100%', maxWidth: '400px', padding: '1.75rem',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {/* Avatar com iniciais + título */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '1.5rem' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%', flexShrink: 0,
            background: 'rgba(240,165,0,0.15)', border: '2px solid rgba(240,165,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.4rem', color: '#f0a500', letterSpacing: '0.04em', lineHeight: 1 }}>
              {usuario.nome ? usuario.nome.trim().split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() : '?'}
            </span>
          </div>
          <div>
            <p style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.1rem', color: '#f0a500', letterSpacing: '0.06em', margin: 0, lineHeight: 1 }}>
              {usuario.perfil === 'admin' ? 'Administrador' : usuario.setor || 'Funcionário'}
            </p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#666', margin: 0, marginTop: '5px' }}>
              {usuario.email || ''}
            </p>
          </div>
        </div>

        {/* Campo de nome */}
        <label style={{ display: 'block', fontSize: '0.78rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
          Nome de exibição
        </label>
        <input
          autoFocus
          value={nome}
          onChange={(e) => { setNome(e.target.value); setErro(''); setOk(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter') salvar(); if (e.key === 'Escape') onClose(); }}
          placeholder="Como você quer ser identificado"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: '#1a1a1a', border: `1px solid ${erro ? '#e05555' : '#2a2a2a'}`,
            borderRadius: '10px', padding: '10px 14px',
            color: '#e0e0e0', fontSize: '0.95rem', fontFamily: "'DM Sans', sans-serif",
            outline: 'none', transition: 'border-color 0.15s',
          }}
          onFocus={(e) => { e.target.style.borderColor = '#f0a500'; }}
          onBlur={(e) => { e.target.style.borderColor = erro ? '#e05555' : '#2a2a2a'; }}
        />

        {erro && (
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: '#e05555', margin: '6px 0 0' }}>
            {erro}
          </p>
        )}

        {/* Botões */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '1.25rem' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, background: 'transparent', border: '1px solid #2a2a2a',
              borderRadius: '10px', padding: '10px', color: '#666',
              fontSize: '0.88rem', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={salvar}
            disabled={salvando || ok}
            style={{
              flex: 2, background: ok ? '#16a34a' : '#f0a500',
              border: 'none', borderRadius: '10px', padding: '10px',
              color: '#0d0d0d', fontSize: '0.88rem', fontWeight: 700,
              cursor: salvando || ok ? 'default' : 'pointer',
              fontFamily: "'DM Sans', sans-serif", transition: 'background 0.2s',
            }}
          >
            {ok ? 'Salvo!' : salvando ? 'Salvando...' : 'Salvar nome'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL: Kanban
// É o "pai" de toda a página. Responsável por:
//   1. Buscar os dados das ordens na API e distribuir por setor
//   2. Gerenciar qual item está selecionado (modal aberto)
//   3. Gerenciar o tema claro/escuro
//   4. Renderizar o header, o board e o painel lateral
//
// "export default" significa que este é o componente padrão exportado pelo arquivo.
// O App.js importa ele e o associa à rota "/kanban".
// ─────────────────────────────────────────────────────────────────────────────
export default function Kanban() {
  // Objeto: { 'Atendimento': [item, item], 'Corte': [], ... }
  const [itensPorSetor, setItensPorSetor] = useState({});
  // Pra cada O.S, quantos/quais itens dela ainda não chegaram na Expedição
  // (nem Concluído) — usado pro card agrupado saber se a O.S como um todo
  // está pronta, não só os itens que aparecem ali.
  const [pendentesPorOrdem, setPendentesPorOrdem] = useState({});
  // Contagem total de itens em produção (exibida no header)
  const [totalItens, setTotalItens]       = useState(0);
  // Controla o loading skeleton enquanto a primeira busca ocorre
  const [carregando, setCarregando]       = useState(true);
  // Guarda a mensagem de erro se a API falhar
  const [erro, setErro]                   = useState('');
  // false = escuro (padrão), true = claro
  const [temaClaro, setTemaClaro]         = useState(false);
  // true = mostra todos os detalhes dos cartões, false = só descrição e anotações
  const [mostrarDetalhes, setMostrarDetalhes] = useState(() => {
    const salvo = localStorage.getItem('mostrarDetalhes');
    return salvo === null ? true : salvo === 'true';
  });
  // Item selecionado para exibir no modal (null = modal fechado)
  const [itemSelecionado, setItemSelecionado] = useState(null);
  // Mensagem de aviso passada ao modal quando SAÍDA é bloqueada (string vazia = sem aviso)
  const [avisoModal, setAvisoModal]       = useState('');
  // Controla a visibilidade dos modais de busca e criação de O.S
  const [modalBuscar, setModalBuscar]     = useState(false);
  const [modalNovaOS, setModalNovaOS]     = useState(false);
  const [modalReconciliar, setModalReconciliar] = useState(false);
  const [numeroParaReconciliar, setNumeroParaReconciliar] = useState('');
  const [pendentesData, setPendentesData] = useState([]);
  const pendentesVistosRef = useRef(null);
  const [checklistKey, setChecklistKey]   = useState(0);
  const [filtro, setFiltro]               = useState('');
  const [filtroModo, setFiltroModo]       = useState('os');
  const [notificacao, setNotificacao]      = useState({ visivel: false, ordens: [] });
  const [modalPerfil, setModalPerfil]     = useState(false);
  const [abaAtiva, setAbaAtiva]           = useState('producao');
  const [ordensConcluidas, setOrdensConcluidas]         = useState([]);
  const [carregandoConcluidos, setCarregandoConcluidos] = useState(false);
  const kanbanScrollRef = useRef(null);
  const [chipAtivo, setChipAtivo]         = useState('30 dias');
  const [filtroChip, setFiltroChip]       = useState(null);
  const [buscaConcluidos, setBuscaConcluidos] = useState('');

  // useNavigate retorna uma função que muda a rota sem recarregar a página
  const navigate = useNavigate();

  // Gera o objeto de tema baseado no estado atual
  const t = getTema(temaClaro);

  const [usuario, setUsuario] = useState(() => JSON.parse(localStorage.getItem('usuario') || '{}'));
  const podeLiberarPedido = usuario.perfil === 'admin' || usuario.acesso_total
    || usuario.setor === 'Atendimento' || (usuario.setores_acesso || []).includes('Atendimento');

  const carregarPendentesData = useCallback(async () => {
    if (!podeLiberarPedido) return;
    try {
      const pendentes = await api.getPendentesData();
      const vistos = pendentesVistosRef.current;
      const novos = vistos === null ? pendentes : pendentes.filter((o) => !vistos.has(o.numero));
      pendentesVistosRef.current = new Set(pendentes.map((o) => o.numero));
      setPendentesData(pendentes);
      if (novos.length > 0) {
        setNotificacao((atual) => {
          const ordens = [...atual.ordens];
          novos.forEach((o) => {
            if (!ordens.some((existente) => existente.numero === o.numero)) {
              ordens.push({ ...o, aguardaData: true });
            }
          });
          return { visivel: true, ordens };
        });
      }
    } catch (err) {
      console.error('Erro ao carregar itens sem data de entrega:', err);
    }
  }, [podeLiberarPedido]);
  function atualizarUsuario(novosDados) {
    const atualizado = { ...usuario, ...novosDados };
    localStorage.setItem('usuario', JSON.stringify(atualizado));
    setUsuario(atualizado);
  }

  // ── Logout ────────────────────────────────────────────────────────────────
  function logout() {
    localStorage.removeItem('token');   // remove o token JWT
    localStorage.removeItem('usuario'); // remove os dados do usuário
    navigate('/login');                 // redireciona para a tela de login
  }

  async function carregarConcluidas(dias) {
    setCarregandoConcluidos(true);
    try {
      const data = await api.getOrdensConcluidas(dias);
      setOrdensConcluidas(data);
    } catch {}
    finally { setCarregandoConcluidos(false); }
  }

  function trocarAba(aba) {
    setAbaAtiva(aba);
    if (aba === 'concluidos') carregarConcluidas(30);
  }

  const CHIPS_PERIODO = [
    { label: 'Hoje',      getDias: () => 2,                        filtrar: os => os.concluido_em && new Date(os.concluido_em).toDateString() === new Date().toDateString() },
    { label: '7 dias',    getDias: () => 7,                        filtrar: null },
    { label: '15 dias',   getDias: () => 15,                       filtrar: null },
    { label: '30 dias',   getDias: () => 30,                       filtrar: null },
    { label: '90 dias',   getDias: () => 90,                       filtrar: null },
    { label: 'Este mês',  getDias: () => new Date().getDate() + 1, filtrar: os => { if (!os.concluido_em) return false; const d = new Date(os.concluido_em), n = new Date(); return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); }},
    { label: 'Todos',     getDias: () => 0,                        filtrar: null },
  ];

  function trocarPeriodo(chip) {
    setChipAtivo(chip.label);
    setFiltroChip(() => chip.filtrar);
    setBuscaConcluidos('');
    carregarConcluidas(chip.getDias());
  }

  function formatarDataConclusao(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  // ── Buscar e agrupar ordens ───────────────────────────────────────────────
  // Esta função é chamada na montagem E a cada 10 segundos (polling).
  // "async" permite usar "await" dentro dela para esperar a API.
  function dispararNotificacao(novas) {
    const u = JSON.parse(localStorage.getItem('usuario') || '{}');
    const podeVer = u.perfil === 'admin' || u.acesso_total ||
      u.setor === 'Atendimento' || (u.setores_acesso || []).includes('Atendimento');
    if (!podeVer || novas.length === 0) return;

    setNotificacao((prev) => ({
      visivel: true,
      ordens: [...prev.ordens, ...novas.map((o) => ({ numero: o.numero, cliente: o.cliente }))],
    }));
  }

  async function carregarOrdens(ehPolling = false) {
    const tentativas = ehPolling ? 1 : 5;

    for (let t = 1; t <= tentativas; t++) {
      try {
        const ordens = await api.getOrdens();

        // Detectar O.S novas comparando com localStorage (chave por usuário)
        const chaveConhecidas = `ordens_conhecidas_${usuario.id}`;
        const salvoAntes = localStorage.getItem(chaveConhecidas);
        const numerosAtuais = ordens.map((o) => o.numero);
        localStorage.setItem(chaveConhecidas, JSON.stringify(numerosAtuais));
        if (salvoAntes !== null) {
          const conhecidas = JSON.parse(salvoAntes);
          const novas = ordens.filter((o) => !o.origem_webhook && !conhecidas.includes(o.numero));
          dispararNotificacao(novas);
        }

        const agrupado = {};
        SETORES.forEach((s) => (agrupado[s] = []));
        let total = 0;

        const SETORES_FINAIS = ['Expedição', 'Concluído'];
        const pendentesPorOrdem = {};

        for (const ordem of ordens) {
          const pendentes = ordem.itens.filter((item) => !SETORES_FINAIS.includes(item.setor_atual));
          if (pendentes.length > 0) {
            pendentesPorOrdem[ordem.numero] = pendentes.map((item) => ({ numero: item.numero, setor_atual: item.setor_atual }));
          }

          for (const item of ordem.itens) {
            if (agrupado[item.setor_atual] !== undefined) {
              agrupado[item.setor_atual].push({ ...item, ordemNumero: ordem.numero, cliente: ordem.cliente, dataEntrega: ordem.origem_webhook ? item.data_entrega : null });
              total++;
            }
          }
        }

        setItensPorSetor(agrupado);
        setPendentesPorOrdem(pendentesPorOrdem);
        setTotalItens(total);
        setErro('');
        setCarregando(false);
        return;
      } catch (err) {
        const isColdStart = err.message === 'Failed to fetch';

        if (ehPolling) { setCarregando(false); return; }

        if (!isColdStart || t === tentativas) {
          setErro(isColdStart ? 'Servidor indisponível. Recarregue a página.' : err.message);
          setCarregando(false);
          return;
        }

        setErro('Servidor inicializando, aguarde...');
        await new Promise(r => setTimeout(r, 4000));
      }
    }
  }

  // ── Polling de dados ──────────────────────────────────────────────────────
  // useEffect com [] roda só na montagem do componente (equivale ao componentDidMount).
  // setInterval dispara carregarOrdens a cada 10 segundos.
  // O return com clearInterval é a "limpeza" que roda quando o componente desmonta,
  // evitando memory leaks (vazamento de memória).
  useEffect(() => {
    api.getConfiguracoes().then((cfg) => {
      urgenciaConfig.baixaMin = parseFloat(cfg.urgencia_baixa_horas) * 60;
      urgenciaConfig.mediaMin = parseFloat(cfg.urgencia_media_horas) * 60;
    }).catch(() => {});
    carregarOrdens();
    const intervalo = setInterval(() => carregarOrdens(true), 10000);
    // Pré-carrega as O.S concluídas dos últimos 30 dias em segundo plano, pra
    // busca por O.S "sem resultado" já saber na hora se ela está em Concluídos
    // (sem chamada de rede na hora de digitar).
    carregarConcluidas(30);
    return () => clearInterval(intervalo);
  }, []);

  useEffect(() => {
    if (!podeLiberarPedido) return;
    carregarPendentesData();
    const intervalo = setInterval(carregarPendentesData, 10000);
    return () => clearInterval(intervalo);
  }, [carregarPendentesData, podeLiberarPedido]);


  useEffect(() => {
    const el = kanbanScrollRef.current;
    if (!el) return;

    const ZONA = 120;
    const VELOCIDADE_MAX = 18;
    let rafId = null;
    let mouseX = null;

    function passo() {
      if (mouseX === null) return;
      const rect = el.getBoundingClientRect();
      const distDir = rect.right - mouseX;
      const distEsq = mouseX - rect.left;
      let velocidade = 0;

      if (distDir < ZONA) velocidade = VELOCIDADE_MAX * (1 - distDir / ZONA);
      else if (distEsq < ZONA) velocidade = -VELOCIDADE_MAX * (1 - distEsq / ZONA);

      if (velocidade !== 0) el.scrollLeft += velocidade;
      rafId = requestAnimationFrame(passo);
    }

    function onMove(e) {
      mouseX = e.clientX;
      if (!rafId) rafId = requestAnimationFrame(passo);
    }

    function onLeave() {
      mouseX = null;
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    }

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    // Container principal: ocupa 100% da altura da tela, layout em coluna
    <div style={{ height: '100vh', background: t.pageBg, display: 'flex', flexDirection: 'column', transition: 'background 0.25s' }}>

      {/* ── Header ─────────────────────────────────────────────────────────
          flexShrink: 0 impede que o header encolha para dar espaço ao conteúdo */}
      <header style={{
        background: t.headerBg,
        borderBottom: `1px solid ${t.headerBorder}`,
        padding: '0 2rem',
        height: '72px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        transition: 'background 0.25s, border-color 0.25s',
      }}>

        {/* Lado esquerdo: logo + nome + indicador ao vivo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Logo quadrado âmbar com as iniciais "GM" */}
          <div style={{
            width: '48px', height: '48px', background: '#f0a500',
            borderRadius: '10px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
          }}>
            <span style={{ fontFamily: "'Bebas Neue', cursive", color: '#0d0d0d', fontSize: '20px', letterSpacing: '1px' }}>GM</span>
          </div>

          <div>
            <p style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '1.5rem', color: t.textHeading, letterSpacing: '0.06em', margin: 0, lineHeight: 1, transition: 'color 0.25s' }}>
              GRÁFICA MÁXIMA
            </p>
            {/* Indicador "ao vivo": bolinha pulsante + contagem de itens */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              {/* className="live-dot" aplica a animação de pulso do index.css */}
              <span className="live-dot" style={{ width: '8px', height: '8px', borderRadius: '50%', background: t.liveDotColor, display: 'inline-block', flexShrink: 0 }} />
              {/* Pluralização simples: "item" ou "itens" dependendo do total */}
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: t.textSecondary, transition: 'color 0.25s' }}>
                {totalItens} {totalItens === 1 ? 'item em produção' : 'itens em produção'}
              </span>
            </div>
          </div>
        </div>

        {/* Centro: botões de ação rápida */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Admin — só para admins */}
          {usuario.perfil === 'admin' && (
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                background: 'transparent', border: '1px solid #2a2a2a', borderRadius: '10px',
                padding: '8px 14px', color: '#888', fontSize: '0.85rem', cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#f0a500'; e.currentTarget.style.color = '#f0a500'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
              Admin
            </button>
          )}
          {/* Relatórios */}
          <button
            onClick={() => navigate('/relatorios')}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              background: 'transparent', border: `1px solid #2a2a2a`, borderRadius: '10px',
              padding: '8px 14px', color: '#888', fontSize: '0.85rem', cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#4f8ef7'; e.currentTarget.style.color = '#4f8ef7'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
            Relatórios
          </button>

          {/* Buscar O.S */}
          <button
            onClick={() => setModalBuscar(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              background: 'transparent', border: `1px solid #2a2a2a`, borderRadius: '10px',
              padding: '8px 14px', color: '#888', fontSize: '0.85rem', cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f0a500'; e.currentTarget.style.color = '#f0a500'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
          >
            {/* Ícone de lupa */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            Buscar O.S
          </button>

          {/* Reconciliar O.S — disponível pra todo mundo, sem restrição de perfil */}
          <button
            onClick={() => { setNumeroParaReconciliar(''); setModalReconciliar(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              background: 'transparent', border: `1px solid #2a2a2a`, borderRadius: '10px',
              padding: '8px 14px', color: '#888', fontSize: '0.85rem', cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00c9db'; e.currentTarget.style.color = '#00c9db'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
          >
            {/* Ícone de sincronização */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Reconciliar O.S
          </button>

          {/* Nova O.S — admin ou quem tem acesso ao Atendimento */}
          {podeLiberarPedido && pendentesData.length > 0 && (
            <button
              onClick={() => setNotificacao({ visivel: true, ordens: pendentesData.map((o) => ({ ...o, aguardaData: true })) })}
              style={{ display: 'flex', alignItems: 'center', gap: '7px', background: 'rgba(0,201,219,0.12)', border: '1px solid #00c9db', borderRadius: '10px', padding: '8px 14px', color: '#00c9db', fontSize: '0.85rem', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 700 }}
            >
              Definir datas ({pendentesData.length})
            </button>
          )}

          {(usuario.perfil === 'admin' || usuario.acesso_total ||
            usuario.setor === 'Atendimento' || (usuario.setores_acesso || []).includes('Atendimento')) && (
            <button
              onClick={() => setModalNovaOS(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                background: '#f0a500', border: '1px solid #f0a500', borderRadius: '10px',
                padding: '8px 14px', color: '#0d0d0d', fontSize: '0.85rem', cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif", fontWeight: 700, transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#d9940a'; e.currentTarget.style.borderColor = '#d9940a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f0a500'; e.currentTarget.style.borderColor = '#f0a500'; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Nova O.S
            </button>
          )}
        </div>

        {/* Lado direito: botão de tema + info do usuário + logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>

          {/* Botão de privacidade — oculta/exibe detalhes dos cartões */}
          <button
            onClick={() => setMostrarDetalhes((v) => {
              localStorage.setItem('mostrarDetalhes', String(!v));
              return !v;
            })}
            title={mostrarDetalhes ? 'Ocultar detalhes' : 'Mostrar detalhes'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '40px', height: '40px',
              background: mostrarDetalhes ? t.toggleBg : 'rgba(240,165,0,0.12)',
              border: `1px solid ${mostrarDetalhes ? t.toggleBorder : 'rgba(240,165,0,0.4)'}`,
              borderRadius: '10px',
              color: mostrarDetalhes ? t.toggleColor : '#f0a500',
              cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f0a500'; e.currentTarget.style.color = '#f0a500'; }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = mostrarDetalhes ? t.toggleBorder : 'rgba(240,165,0,0.4)';
              e.currentTarget.style.color = mostrarDetalhes ? t.toggleColor : '#f0a500';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {mostrarDetalhes ? (
                <>
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </>
              ) : (
                <>
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </>
              )}
            </svg>
          </button>

          {/* Botão de alternância de tema claro/escuro */}
          <button
            onClick={() => setTemaClaro((v) => !v)} // inverte o valor booleano
            title={temaClaro ? 'Tema escuro' : 'Tema claro'} // tooltip nativo do browser
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '40px', height: '40px', background: t.toggleBg,
              border: `1px solid ${t.toggleBorder}`, borderRadius: '10px',
              color: t.toggleColor, cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f0a500'; e.currentTarget.style.color = '#f0a500'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.toggleBorder; e.currentTarget.style.color = t.toggleColor; }}
          >
            <IconeTema claro={temaClaro} />
          </button>

          {/* Chip de perfil — clicável para abrir o modal */}
          <button
            onClick={() => setModalPerfil(true)}
            title="Meu perfil"
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              background: temaClaro ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${temaClaro ? '#e0d8cc' : '#2a2a2a'}`,
              borderRadius: '12px', padding: '7px 12px 7px 8px',
              cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f0a500'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = temaClaro ? '#e0d8cc' : '#2a2a2a'; }}
          >
            {/* Avatar com iniciais */}
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
              background: 'rgba(240,165,0,0.15)', border: '1.5px solid rgba(240,165,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontFamily: "'Bebas Neue', cursive", fontSize: '0.95rem', color: '#f0a500', lineHeight: 1 }}>
                {usuario.nome ? usuario.nome.trim().split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() : '?'}
              </span>
            </div>
            {/* Nome e setor */}
            <div style={{ textAlign: 'left' }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: t.textHeading, fontWeight: 600, margin: 0, lineHeight: 1, transition: 'color 0.25s' }}>
                {usuario.nome}
              </p>
              {(usuario.setor || usuario.perfil === 'admin') && (
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: t.textSecondary, margin: 0, marginTop: '3px', lineHeight: 1, transition: 'color 0.25s' }}>
                  {usuario.perfil === 'admin' ? 'Administrador' : usuario.setor}
                </p>
              )}
            </div>
          </button>

          {/* Botão de logout: remove dados e redireciona */}
          <button
            onClick={logout}
            style={{
              background: 'transparent', border: `1px solid ${t.logoutBorder}`, borderRadius: '9px',
              padding: '8px 18px', color: t.logoutColor, fontSize: '0.85rem', cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.04em', transition: 'border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#e03333'; e.currentTarget.style.color = '#e03333'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.logoutBorder; e.currentTarget.style.color = t.logoutColor; }}
          >
            Sair
          </button>
        </div>
      </header>

      {/* ── Abas ─────────────────────────────────────────────────────────── */}
      <div style={{ background: t.headerBg, borderBottom: `1px solid ${t.headerBorder}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', padding: '0 1.5rem' }}>
          {[
            { id: 'producao',  label: 'Em Produção' },
            { id: 'concluidos', label: 'Concluídos' },
          ].map(a => (
            <button
              key={a.id}
              onClick={() => trocarAba(a.id)}
              style={{
                background: 'transparent', border: 'none',
                borderBottom: `2px solid ${abaAtiva === a.id ? '#f0a500' : 'transparent'}`,
                padding: '10px 18px', cursor: 'pointer',
                color: abaAtiva === a.id ? '#f0a500' : '#888',
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem',
                fontWeight: abaAtiva === a.id ? 700 : 400,
                transition: 'all 0.15s',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Corpo: Kanban + Painel lateral ──────────────────────────────────
          flex: 1 faz este container ocupar todo o espaço abaixo do header.
          overflow: hidden impede que o scroll vaze para fora do container. */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Aba Concluídos ─────────────────────────────────────────────── */}
        {abaAtiva === 'concluidos' && (
          <main style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', fontFamily: "'DM Sans', sans-serif" }}>

            {/* Filtros */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
              {/* Chips de período */}
              <div style={{ display: 'flex', gap: '6px' }}>
                {CHIPS_PERIODO.map(chip => (
                  <button
                    key={chip.label}
                    onClick={() => trocarPeriodo(chip)}
                    style={{
                      padding: '5px 13px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.78rem',
                      fontFamily: "'DM Sans', sans-serif", fontWeight: chipAtivo === chip.label ? 700 : 400,
                      border: `1px solid ${chipAtivo === chip.label ? '#4ade80' : '#2a2a2a'}`,
                      background: chipAtivo === chip.label ? 'rgba(74,222,128,0.12)' : 'transparent',
                      color: chipAtivo === chip.label ? '#4ade80' : '#888',
                      transition: 'all 0.15s',
                    }}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>

              {/* Campo de busca */}
              <div style={{ position: 'relative', flex: '1', maxWidth: '320px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"
                  style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  value={buscaConcluidos}
                  onChange={e => setBuscaConcluidos(e.target.value)}
                  placeholder="Buscar por O.S ou cliente..."
                  autoComplete="off"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: t.cardBg, border: `1px solid ${t.cardBorder}`,
                    borderRadius: '20px', padding: '6px 14px 6px 32px',
                    color: t.textPrimary, fontSize: '0.83rem',
                    fontFamily: "'DM Sans', sans-serif", outline: 'none',
                  }}
                  onFocus={e => e.target.style.borderColor = '#4ade80'}
                  onBlur={e => e.target.style.borderColor = t.cardBorder}
                />
                {buscaConcluidos && (
                  <button onClick={() => setBuscaConcluidos('')}
                    style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}>
                    ×
                  </button>
                )}
              </div>
            </div>

            {carregandoConcluidos && (
              <p style={{ color: t.textMuted, textAlign: 'center', paddingTop: '3rem' }}>Carregando...</p>
            )}
            {!carregandoConcluidos && (() => {
              const termo = buscaConcluidos.toLowerCase().trim();
              let filtradas = filtroChip ? ordensConcluidas.filter(filtroChip) : ordensConcluidas;
              if (termo) {
                filtradas = filtradas.filter(os =>
                  os.numero.toLowerCase().startsWith(termo) ||
                  os.cliente.toLowerCase().includes(termo)
                );
              }

              if (filtradas.length === 0) return (
                <div style={{ textAlign: 'center', paddingTop: '4rem' }}>
                  <p style={{ color: t.textMuted, fontSize: '0.95rem' }}>
                    {termo ? `Nenhuma O.S encontrada para "${buscaConcluidos}".` : 'Nenhuma O.S concluída neste período.'}
                  </p>
                </div>
              );

              return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
                {filtradas.map(os => (
                  <div key={os.id} style={{
                    background: t.cardBg, border: '1px solid #4ade8035',
                    borderLeft: '4px solid #4ade80', borderRadius: '12px', padding: '16px 18px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <p style={{ margin: 0, fontFamily: "'Bebas Neue', cursive", fontSize: '1.35rem', color: '#4ade80', letterSpacing: '0.06em' }}>
                          O.S {os.numero}
                        </p>
                        <p style={{ margin: '3px 0 0 0', fontSize: '0.88rem', color: '#ccc', fontWeight: 600 }}>{os.cliente}</p>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <p style={{ margin: 0, fontSize: '0.7rem', color: '#4ade80', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Concluído</p>
                        <p style={{ margin: '3px 0 0 0', fontSize: '0.75rem', color: '#aaa' }}>{formatarDataConclusao(os.concluido_em)}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {os.itens.map(item => (
                        <div key={item.numero} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ade80', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.8rem', color: '#4ade80', fontWeight: 600, flexShrink: 0 }}>{item.numero}</span>
                          <span style={{ fontSize: '0.8rem', color: '#666' }}>—</span>
                          <span style={{ fontSize: '0.8rem', color: '#ccc', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.descricao}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              );
            })()}
          </main>
        )}

        {/* Área do Kanban: scroll horizontal para as colunas, vertical para os itens */}
        {abaAtiva === 'producao' && <main ref={kanbanScrollRef} className="kanban-scroll" style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', padding: '1.5rem' }}>

          {/* Barra de filtro inline */}
          {!carregando && !erro && (() => {
            const termo = filtro.toLowerCase().trim();
            const modoFiltro = filtroModo;
            const placeholders = { os: 'Número da O.S...', descricao: 'Descrição do produto...', cliente: 'Nome do cliente...' };
            const totalFiltrado = termo
              ? SETORES.reduce((acc, s) => acc + (itensPorSetor[s] || []).filter(item =>
                  modoFiltro === 'os'        ? item.ordemNumero?.toLowerCase().includes(termo) :
                  modoFiltro === 'descricao' ? item.descricao?.toLowerCase().startsWith(termo) :
                  item.cliente?.toLowerCase().startsWith(termo)
                ).length, 0)
              : null;
            // Busca por O.S sem resultado em produção: já sabe na hora (sem chamada de
            // rede) se ela está entre as concluídas pré-carregadas dos últimos 30 dias.
            // Match EXATO (não substring) — "512" não pode acender aviso de "5120".
            const osConcluida = (termo && modoFiltro === 'os' && totalFiltrado === 0)
              ? ordensConcluidas.find(o => o.numero.toLowerCase() === termo)
              : null;
            const chips = [
              { id: 'os',        label: 'O.S' },
              { id: 'descricao', label: 'Descrição' },
              { id: 'cliente',   label: 'Cliente' },
            ];
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '1.25rem', minWidth: 'max-content' }}>
                {/* Chips de modo */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  {chips.map(({ id, label }) => {
                    const ativo = modoFiltro === id;
                    return (
                      <button
                        key={id}
                        onClick={() => { setFiltroModo(id); setFiltro(''); }}
                        style={{
                          fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', fontWeight: 700,
                          padding: '3px 11px', borderRadius: '20px', cursor: 'pointer',
                          border: `1px solid ${ativo ? '#f0a500' : t.colBorder}`,
                          color: ativo ? '#f0a500' : t.textSecondary,
                          background: ativo ? 'rgba(240,165,0,0.1)' : 'transparent',
                          transition: 'all 0.15s', letterSpacing: '0.03em',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* Campo de busca */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ position: 'relative', width: '320px', flexShrink: 0 }}>
                    <svg
                      width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="#555" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                      style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                    >
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input
                      value={filtro}
                      onChange={(e) => setFiltro(e.target.value)}
                      placeholder={placeholders[modoFiltro]}
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        background: t.cardBg, border: `1px solid ${t.cardBorder}`,
                        borderRadius: '10px', padding: '0.6rem 1rem 0.6rem 2.4rem',
                        color: t.textPrimary, fontSize: '0.88rem',
                        fontFamily: "'DM Sans', sans-serif", outline: 'none',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#f0a500'}
                      onBlur={(e) => e.target.style.borderColor = t.cardBorder}
                    />
                    {filtro && (
                      <button
                        onClick={() => setFiltro('')}
                        style={{
                          position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                          background: 'transparent', border: 'none', color: '#555',
                          fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, padding: 0,
                        }}
                      >×</button>
                    )}
                  </div>
                  {termo && (
                    osConcluida ? (
                      <button
                        onClick={() => trocarAba('concluidos')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.35)',
                          borderRadius: '20px', padding: '3px 12px', cursor: 'pointer',
                          fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 600,
                          color: '#4ade80', whiteSpace: 'nowrap',
                        }}
                      >
                        ✓ A O.S {osConcluida.numero} já está concluída — clique aqui para ver
                      </button>
                    ) : (
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: totalFiltrado > 0 ? '#f0a500' : '#555', whiteSpace: 'nowrap' }}>
                        {totalFiltrado > 0
                          ? `${totalFiltrado} ${totalFiltrado === 1 ? 'item encontrado' : 'itens encontrados'}`
                          : 'Nenhum item encontrado'}
                      </span>
                    )
                  )}
                </div>
              </div>
            );
          })()}

          {(() => {
            const setoresVisiveis = (usuario.perfil === 'admin' || usuario.acesso_total)
              ? SETORES
              : SETORES.filter(s => s === usuario.setor || (usuario.setores_acesso || []).includes(s));
            return carregando ? (
              // Enquanto carrega: mostra o skeleton de colunas fantasma
              <Loading t={t} setoresVisiveis={setoresVisiveis} />
            ) : erro ? (
              // Se houve erro na API: mostra a mensagem em vermelho
              <div style={{ background: 'rgba(224,51,51,0.08)', border: '1px solid rgba(224,51,51,0.2)', borderRadius: '10px', padding: '1rem 1.25rem', color: '#e05555', fontSize: '0.9rem', fontFamily: "'DM Sans', sans-serif", maxWidth: '400px' }}>
                {erro}
              </div>
            ) : (() => {

            const termo = filtro.toLowerCase().trim();
            // Com busca ativa: pesquisa em todos os setores e exibe qualquer coluna com resultado.
            // Sem busca: exibe apenas os setores do usuário.
            const setoresParaRenderizar = termo ? SETORES : setoresVisiveis;
            const itensFiltrados = termo
              ? Object.fromEntries(SETORES.map(s => [s, (itensPorSetor[s] || []).filter(item =>
                  filtroModo === 'os'        ? item.ordemNumero?.toLowerCase().includes(termo) :
                  filtroModo === 'descricao' ? item.descricao?.toLowerCase().startsWith(termo) :
                  item.cliente?.toLowerCase().startsWith(termo)
                )]))
              : itensPorSetor;

            return (
              // Board principal: uma ColunaSetor para cada setor
              <div style={{ display: 'flex', gap: '20px', alignItems: 'stretch', minWidth: 'max-content' }}>
                {setoresParaRenderizar
                  .filter(setor => !termo || (itensFiltrados[setor] || []).length > 0)
                  .map((setor) => (
                    <ColunaSetor
                      key={setor}
                      setor={setor}
                      itens={itensFiltrados[setor] || []}
                      t={t}
                      onAbrirModal={setItemSelecionado}
                      mostrarDetalhes={mostrarDetalhes}
                      onRecarregar={carregarOrdens}
                      pendentesPorOrdem={pendentesPorOrdem}
                    />
                  ))}
              </div>
            );
          })();
          })()}
        </main>}

      </div>

      {/* ── Modal: renderizado fora do layout principal (portal seria ideal,
          mas aqui funciona bem porque o z-index 1000 garante que fica acima)
          Só renderiza quando há um item selecionado (itemSelecionado !== null) */}
      {itemSelecionado && (
        <ModalItem
          item={itemSelecionado}
          t={t}
          aviso={avisoModal}  // string vazia = sem banner de aviso
          // Ao fechar: limpa tanto o item quanto o aviso
          onClose={() => { setItemSelecionado(null); setAvisoModal(''); }}
          onRetrograde={carregarOrdens}
          onBiparSucesso={carregarOrdens}
        />
      )}

      {/* Modal de busca de O.S */}
      {modalBuscar && (
        <ModalBuscarOS
          onClose={() => setModalBuscar(false)}
          onDeletado={carregarOrdens}
        />
      )}

      {/* Modal de reconciliação de O.S */}
      {modalReconciliar && (
        <ModalReconciliarOS
          onClose={() => { setModalReconciliar(false); setNumeroParaReconciliar(''); }}
          numeroInicial={numeroParaReconciliar}
        />
      )}

      {/* Modal de criação de nova O.S — ao criar, recarrega o Kanban imediatamente */}
      {modalNovaOS && (
        <ModalNovaOS
          onClose={() => setModalNovaOS(false)}
          onCriado={carregarOrdens}
          onIrParaReconciliar={(num) => {
            setModalNovaOS(false);
            setNumeroParaReconciliar(num);
            setModalReconciliar(true);
          }}
        />
      )}

      {/* Modal de perfil — editar nome de exibição */}
      {modalPerfil && (
        <ModalPerfil
          usuario={usuario}
          onClose={() => setModalPerfil(false)}
          onSalvar={atualizarUsuario}
        />
      )}

      <NotificacaoNovasOS
        notificacao={notificacao}
        onFechar={() => setNotificacao({ visivel: false, ordens: [] })}
        onDataSalva={async (numero) => {
          setNotificacao((atual) => {
            const ordens = atual.ordens.filter((o) => o.numero !== numero);
            return { visivel: ordens.length > 0, ordens };
          });
          setPendentesData((atual) => atual.filter((o) => o.numero !== numero));
          await Promise.all([carregarOrdens(), carregarPendentesData()]);
        }}
      />
    </div>
  );
}
